import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import { stocktakeSessionsService, beverageProductsService, siteInventoryService, deliveriesService, creditsService, suppliersService, stocktakingSettingsService } from '../services';
import stocktakeOfflineService from '../services/stocktakeOfflineService';
import { subcategoryComparator } from '../lib/subcategoryOrder';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowRightLeft, Loader2, Plus, Trash2, Search, X, ChevronDown, ChevronRight,
  Truck, RotateCcw, FileText, RefreshCw, Save, PackagePlus,
} from 'lucide-react';
import { getApiError, formatDDMMYYYY } from '@/lib/utils';
import AddFromMasterModal from '../components/AddFromMasterModal';
import QuickAddSupplierModal from '../components/QuickAddSupplierModal';

const fmt = (val) => {
  if (val == null) return '\u00A30.00';
  return `\u00A3${Number(val).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// iter366 — Cost cascade conversion helper. The `/site-cost-map` API
// returns each product's most-recent cost together with the unit_label
// it was recorded against (e.g. opening stock saved a bottle cost while
// the new delivery line defaults to a CASE). We rescale the cost into
// the target unit using `conversion_to_base` so the auditor sees the
// right per-unit number, e.g. £1/bottle → £24/case for a 24-pack.
//
// Production bug (Mercure Tunbridge, iter366 v1): Budweiser NRB
// defaulted to "case" but `cost_price` came in as the bottle cost from
// Opening Stock and was displayed unconverted.
//
// iter366 v2 — Production data carries free-text legacy labels
// (`unit_label='bottle'`) for products whose ACTUAL unit_type is the
// size-suffixed identifier (`bottle_330ml`, `bottle_500ml`, `can_330ml`).
// Old Speckled HEN, Peroni Gluten FREE, Singha all fell through the
// exact-match path. Added a "family" fallback that maps generic family
// names (bottle / can / keg / bottle_500ml etc.) to the product's
// canonical unit when there's exactly one unambiguous candidate. We
// intentionally bail when multiple bottle-like units exist on a single
// product (rare but possible) — better to leave the raw cost than
// silently pick the wrong one.
function findUnitByLabel(product, label) {
  if (!label || !product?.units?.length) return null;
  const lower = String(label).trim().toLowerCase();
  // 1. Exact (case-insensitive) match.
  const exact = product.units.find(u => (u.unit_type || '').trim().toLowerCase() === lower);
  if (exact) return exact;
  // 2. Family-prefix match — split on '_' and '/' so 'bottle_330ml',
  //    'bottle_500ml', 'keg/cask', 'keg_50ltr', 'can_330ml' all
  //    normalise to their family token (bottle / keg / can).
  const family = lower.split(/[_/]/)[0];
  if (!family) return null;
  const candidates = product.units.filter(u => {
    const ut = (u.unit_type || '').trim().toLowerCase();
    if (!ut) return false;
    const utFam = ut.split(/[_/]/)[0];
    return utFam === family;
  });
  if (candidates.length === 1) return candidates[0];
  if (candidates.length > 1) {
    // Prefer the bar-side (count) unit when more than one candidate
    // exists. If still ambiguous → return null (caller will pass-through).
    const def =
      candidates.find(u => u.is_default_count_unit)
      || candidates.find(u => u.is_base_stock_unit);
    if (def) return def;
  }
  return null;
}

function convertCascadeCost(cascade, targetUnitLabel, product, emptyWhenNoMatch = false) {
  // iter825 — Delivery-note callers pass emptyWhenNoMatch=true so a
  // product with no qualifying DN line shows a blank, editable cost
  // field instead of a misleading "0". Credit notes keep the old '0'.
  if (cascade == null || cascade.cost_price == null) return emptyWhenNoMatch ? '' : '0';
  const rawCost = Number(cascade.cost_price);
  if (!Number.isFinite(rawCost) || rawCost <= 0) return String(cascade.cost_price);
  const srcLabel = cascade.unit_label;
  // No source unit recorded, or src == target (case-insensitive) — pass-through.
  if (!srcLabel || !targetUnitLabel || String(srcLabel).trim().toLowerCase() === String(targetUnitLabel).trim().toLowerCase()) {
    return String(cascade.cost_price);
  }
  const srcUnit = findUnitByLabel(product, srcLabel);
  const tgtUnit = findUnitByLabel(product, targetUnitLabel);
  const srcConv = parseFloat(srcUnit?.conversion_to_base);
  const tgtConv = parseFloat(tgtUnit?.conversion_to_base);
  if (!(srcConv > 0) || !(tgtConv > 0)) {
    // Couldn't resolve one or both units — fall back to raw cost.
    // Auditor will see it and can override; the alternative (silently
    // multiplying by a wrong ratio) is worse.
    return String(cascade.cost_price);
  }
  // Same effective unit after family-match (e.g. cascade says 'bottle'
  // and the target is also 'bottle' on a single-size product). Skip
  // the multiply.
  if (srcUnit === tgtUnit || srcConv === tgtConv) {
    return String(cascade.cost_price);
  }
  const converted = rawCost * (tgtConv / srcConv);
  // 4 dp matches the case↔bottle toggle handler.
  return String(Math.round(converted * 10000) / 10000);
}

// ── Delivery/Credit Note Component ──
// Shows a header (supplier, ref, date) + product grid matching Sales panel pattern
function DeliveryNote({ note, type, products, productMap, costMap, suppliers, isEditable, onUpdate, onDelete, onSaved, siteId, sessionId, periodFrom, periodTo, isOfflineMode, onSaveStart, onSaveEnd, vatRate, invoiceBasis, onProductAdded, assignedProductIds, persistNewNote }) {
  const [expanded, setExpanded] = useState(note._new || false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [saving, setSaving] = useState(false);
  // iter362h — Track silent autosave state ("idle" | "saving" | "saved")
  // so we can show a tiny indicator in place of the old explicit
  // "Save & Post" / "Save Changes" button. The user expects every
  // wizard transaction to silently persist on edit + be flushed by
  // the wizard footer's "Save & Next" button — no per-note save
  // button needed.
  const [autosaveState, setAutosaveState] = useState('idle');
  const autosaveTimer = useRef(null);
  // iter363 — Track whether the *user* has actually edited this note
  // since the last save/load. Without this, every re-render of the
  // parent (which spreads `{...item}` into the `note` prop) trips the
  // autosave effect, which then triggers loadData() and re-renders
  // again — creating an endless "Saving… → Loading deliveries…" flash
  // and a write-amplification loop on the server.
  const userEditedRef = useRef(false);
  // "Add to Inventory" nested modal state — opens AddFromMasterModal
  // without closing the delivery note dialog.
  const [noteAddFromMasterOpen, setNoteAddFromMasterOpen] = useState(false);
  // Quick-add supplier — local to this note so opening the modal does not
  // close/affect any other expanded note.
  const [quickAddSupplierOpen, setQuickAddSupplierOpen] = useState(false);
  const [localNewSuppliers, setLocalNewSuppliers] = useState([]);
  // Merge parent supplier list with any suppliers created in this session.
  const allSuppliers = useMemo(() => {
    const map = new Map((suppliers || []).map(s => [s.id, s]));
    localNewSuppliers.forEach(s => map.set(s.id, s));
    return Array.from(map.values()).sort((a, b) => (a.supplier_name || '').localeCompare(b.supplier_name || ''));
  }, [suppliers, localNewSuppliers]);
  const handleQuickSupplierCreated = (newSupplier) => {
    setLocalNewSuppliers(prev => [...prev, newSupplier]);
    updateHeader('supplier_id', String(newSupplier.id));
  };
  // iter426 (Iteration B #7) — Supplier-scoped cost cascade for CREDIT
  // notes only. When the user picks a supplier on a credit note, fetch
  // the ORG-WIDE last-cost map for that supplier so a cost credited at
  // Bar 1 yesterday pre-fills the same product at Bar 2 today. Falls
  // back to the parent's site-scoped costMap when no supplier is
  // selected. iter825 — Delivery Notes are explicitly EXCLUDED from
  // this cross-site cascade: a manual DN's cost default must never
  // source from another site/client. For type === 'delivery', `costMap`
  // is already the strict same-site/same-unit/DN-only map the parent
  // passes down (see `dnCostMap` in the parent component), so
  // `effectiveCostMap` is just that map, unmodified.
  const [supplierCostMap, setSupplierCostMap] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const sid = note.supplier_id ? parseInt(note.supplier_id) : null;
    if (type !== 'credit' || !sid || !siteId || isOfflineMode) {
      setSupplierCostMap(null);
      return;
    }
    deliveriesService
      .siteCostMap(siteId, sid, note.date || undefined)
      .then(res => { if (!cancelled) setSupplierCostMap(res || {}); })
      .catch(() => { if (!cancelled) setSupplierCostMap(null); });
    return () => { cancelled = true; };
  }, [type, note.supplier_id, siteId, isOfflineMode]);
  // Merge: supplier-scoped takes priority, site costMap is fallback.
  const effectiveCostMap = useMemo(() => {
    if (type !== 'credit' || !supplierCostMap) return costMap || {};
    return { ...(costMap || {}), ...supplierCostMap };
  }, [type, costMap, supplierCostMap]);

  const [renderLimit, setRenderLimit] = useState(80);
  const { toast } = useToast();

  // iter_postmix — prevent auto-focus stealing from search box.
  // Set to true once the initial auto-focus has been done for this note.
  const autoFocusedRef = useRef(false);
  useEffect(() => { autoFocusedRef.current = false; }, [note._key, note.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // iter_postmix v2 — resizable modal with localStorage-backed size persistence.
  // Defaults to a large size; user can drag the resize handle and size is remembered.
  const MODAL_SIZE_KEY = 'delivery-modal-dimensions-v2';
  const [modalSize, setModalSize] = useState(() => {
    try {
      const saved = localStorage.getItem(MODAL_SIZE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const dialogContentRef = useRef(null);
  // Attach ResizeObserver when dialog is open to persist size on drag-resize
  useEffect(() => {
    if (!expanded) return;
    const el = dialogContentRef.current;
    if (!el) return;
    let saveTimer = null;
    const obs = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 400 && height > 300) {
          clearTimeout(saveTimer);
          saveTimer = setTimeout(() => {
            const newSize = { width: Math.round(width), height: Math.round(height) };
            setModalSize(newSize);
            try { localStorage.setItem(MODAL_SIZE_KEY, JSON.stringify(newSize)); } catch {}
          }, 300);
        }
      }
    });
    obs.observe(el);
    return () => { obs.disconnect(); clearTimeout(saveTimer); };
  }, [expanded]);

  // iter_postmix — per-line edit modes: 'none' (cost/total greyed) | 'cost' (F4) | 'full' (F6)
  const [lineEditModes, setLineEditModes] = useState({});
  const getLineEditMode = (pid) => lineEditModes[String(pid)] || 'none';
  const setLineEditMode = (pid, mode) => setLineEditModes(prev => ({ ...prev, [String(pid)]: mode }));

  // iter426 — Delivery date ring-fencing. When the note's date is
  // outside the stocktake period, block save and surface a banner so
  // the auditor must adjust the date. Prevents pre/post-period
  // deliveries from bleeding across stocktakes (balance forward owns
  // the period boundary, not individual DNs).
  const dateOutOfPeriod = useMemo(() => {
    if (!note.date || !periodFrom || !periodTo) return false;
    const d = note.date;
    return d < periodFrom || d > periodTo;
  }, [note.date, periodFrom, periodTo]);

  const availableCategories = useMemo(() => {
    const cats = new Set();
    products.forEach(p => {
      const cat = typeof p.category === 'object' ? p.category?.value : p.category;
      if (cat) cats.add(cat);
    });
    return [...cats].sort();
  }, [products]);

  const availableSubcategories = useMemo(() => {
    const subs = new Set();
    products.forEach(p => { if (p.subcategory) subs.add(p.subcategory); });
    // Use canonical subcategory order (matches the count/sales panel display order),
    // NOT alphabetical — alphabetical was the previous incorrect behaviour.
    return [...subs].sort(subcategoryComparator);
  }, [products]);

  const activeFilterCount = selectedCategories.length + selectedSubcategories.length;

  // iter363e — Always render every site product (matches the Sales
  // panel pattern: bucketed by subcategory, lines with qty highlighted
  // in green). For NEW notes the parent's `addNewNote` already seeds
  // all products with cascade-filled cost prices; for EXISTING notes
  // we layer saved-line data on top of the catalogue and pre-fill
  // cost for placeholder rows from `costMap` (last delivery → opening
  // stock → 0). The auditor can still edit cost manually.
  const mergedLines = useMemo(() => {
    const saved = note.lines || [];
    if (!products || products.length === 0) return saved;
    const savedByPid = new Map();
    saved.forEach(l => { savedByPid.set(String(l.beverage_product_id), l); });
    const out = [];
    for (const p of products) {
      const key = String(p.id);
      const existing = savedByPid.get(key);
      if (existing) {
        out.push(existing);
      } else {
        const cascade = effectiveCostMap?.[key];
        // iter363e — cost cascade fallback chain (last delivery →
        // opening stock → 0). iter366 — convert source cost into the
        // line's target unit so a bottle-priced cascade no longer
        // appears unconverted on a case row. iter426 — when a
        // supplier is set the cascade is supplier-scoped (org-wide).
        const targetUnit = getDefaultUnit(p);
        const cost = convertCascadeCost(cascade, targetUnit, p, type === 'delivery');
        out.push({
          beverage_product_id: key,
          product_name: p.product_name,
          product_code: p.product_code || '',
          quantity: '',
          cost_price: cost,
          cost_source: cascade?.source || null,
          cost_delivery_date: cascade?.delivery_date || null,
          cost_delivery_ref: cascade?.reference_number || null,
          cost_delivery_status: cascade?.delivery_status || null,
          unit_label: targetUnit,
          line_total: 0,
          _augmented: true,
        });
      }
    }
    return out;
  }, [note.lines, products, effectiveCostMap]);

  // Lines with original index
  const visibleLines = useMemo(() => {
    return mergedLines.map((l, idx) => ({ ...l, _origIdx: idx })).filter(line => {
      const prod = productMap[parseInt(line.beverage_product_id)];
      if (selectedCategories.length > 0) {
        const cat = typeof prod?.category === 'object' ? prod?.category?.value : prod?.category;
        if (!cat || !selectedCategories.includes(cat)) return false;
      }
      if (selectedSubcategories.length > 0) {
        if (!prod?.subcategory || !selectedSubcategories.includes(prod.subcategory)) return false;
      }
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const name = (prod?.product_name || line.product_name || '').toLowerCase();
        const code = (prod?.product_code || line.product_code || '').toLowerCase();
        if (!name.includes(q) && !code.includes(q)) return false;
      }
      return true;
    });
  }, [mergedLines, productMap, selectedCategories, selectedSubcategories, searchTerm]);

  // iter363e — Group visible lines by subcategory; sticky heading +
  // count per group, "Other" sinks to the bottom. Mirrors the Sales
  // panel layout (WizardSalesPanel.groupedVisibleEntries).
  const groupedVisibleLines = useMemo(() => {
    const groups = new Map();
    for (const line of visibleLines) {
      const prod = productMap[parseInt(line.beverage_product_id)];
      const sub = (prod?.subcategory && String(prod.subcategory).trim()) || 'Other';
      if (!groups.has(sub)) groups.set(sub, []);
      groups.get(sub).push(line);
    }
    for (const arr of groups.values()) {
      arr.sort((a, b) => {
        const an = (productMap[parseInt(a.beverage_product_id)]?.product_name || a.product_name || '').toLowerCase();
        const bn = (productMap[parseInt(b.beverage_product_id)]?.product_name || b.product_name || '').toLowerCase();
        return an.localeCompare(bn);
      });
    }
    const keys = [...groups.keys()].sort((a, b) => {
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      // Use canonical subcategory display order (NOT alphabetical) — ST-0028 fix
      return subcategoryComparator(a, b);
    });
    return keys.map(k => ({ subcategory: k, lines: groups.get(k) }));
  }, [visibleLines, productMap]);

  // iter363e — Flat list of Qty input IDs in the order they appear
  // on screen. Used by the ArrowDown / ArrowUp / Enter key handler so
  // the auditor can paddle through quantities column-wise without
  // ever leaving the keyboard.
  const qtyFocusOrder = useMemo(() => {
    const ids = [];
    for (const group of groupedVisibleLines) {
      for (const l of group.lines) ids.push(`${type}-qty-${l.beverage_product_id}`);
    }
    return ids;
  }, [groupedVisibleLines, type]);

  const focusQtyByOffset = (currentPid, offset) => {
    const id = `${type}-qty-${currentPid}`;
    const idx = qtyFocusOrder.indexOf(id);
    if (idx === -1) return;
    const nextIdx = Math.max(0, Math.min(qtyFocusOrder.length - 1, idx + offset));
    const next = qtyFocusOrder[nextIdx];
    if (!next || next === id) return;
    const el = document.querySelector(`[data-testid="${next}"]`);
    if (el && typeof el.focus === 'function') {
      el.focus();
      if (typeof el.select === 'function') el.select();
    }
  };

  // iter363f — Sibling-cell focus helper. When the auditor presses
  // ← / → from the Qty input, jump to the Unit (← of Qty) or Cost
  // (→ of Qty) cell on the same row so they can adjust those
  // fields without lifting fingers off the keyboard.
  const focusSiblingCell = (currentPid, direction) => {
    const targetTestId = direction === 'left'
      ? null  // there's no editable cell strictly left of Qty other than the unit Select trigger
      : `${type}-cost-${currentPid}`;
    if (direction === 'left') {
      // Unit cell renders as a Radix Select trigger button — look up
      // by aria-label fallback (we don't attach a unique testid per
      // row).
      const row = document.querySelector(`[data-testid="${type}-line-${currentPid}"]`);
      const trigger = row?.querySelector('[role="combobox"]');
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
      return;
    }
    if (!targetTestId) return;
    const el = document.querySelector(`[data-testid="${targetTestId}"]`);
    if (el && typeof el.focus === 'function') {
      el.focus();
      if (typeof el.select === 'function') el.select();
    }
  };

  const handleQtyKeyDown = (e, productId) => {
    if (e.key === 'ArrowDown' || (e.key === 'Enter' && !e.shiftKey)) {
      e.preventDefault();
      focusQtyByOffset(productId, +1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusQtyByOffset(productId, -1);
    } else if (e.key === 'PageDown') {
      // iter363f — viewport-scale jumps (10 lines at a time) so big
      // catalogues are navigable from the keyboard.
      e.preventDefault();
      focusQtyByOffset(productId, +10);
    } else if (e.key === 'PageUp') {
      e.preventDefault();
      focusQtyByOffset(productId, -10);
    } else if (e.key === 'Tab' && !e.shiftKey) {
      // iter_postmix — Tab moves to next qty (unless full line edit mode)
      if (getLineEditMode(productId) === 'full') {
        e.preventDefault();
        const el = document.querySelector(`[data-testid="${type}-cost-${productId}"]`);
        if (el) { el.focus(); el.select?.(); }
      } else {
        e.preventDefault();
        focusQtyByOffset(productId, +1);
      }
    } else if (e.key === 'F4') {
      // iter_postmix — F4: activate cost price editing
      e.preventDefault();
      setLineEditMode(productId, 'cost');
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="${type}-cost-${productId}"]`);
        if (el) { el.focus(); el.select?.(); }
      }, 10);
    } else if (e.key === 'F6') {
      // iter_postmix — F6: full line edit mode (Tab through all fields)
      e.preventDefault();
      setLineEditMode(productId, 'full');
    } else if (e.key === 'ArrowRight') {
      // Only intercept when the cursor is at the END of the value —
      // otherwise the user is editing inside the number and ← / →
      // should move the caret, not the focus.
      const inp = e.currentTarget;
      const len = (inp.value || '').length;
      if (inp.selectionStart === len && inp.selectionEnd === len) {
        e.preventDefault();
        focusSiblingCell(productId, 'right');
      }
    } else if (e.key === 'ArrowLeft') {
      const inp = e.currentTarget;
      if (inp.selectionStart === 0 && inp.selectionEnd === 0) {
        e.preventDefault();
        focusSiblingCell(productId, 'left');
      }
    }
  };

  // iter363f — same key map for the Cost input so PageUp/PageDown
  // and ↑/↓ also work when focus is on Cost — keeps the keyboard
  // journey consistent across the row.
  const handleCostKeyDown = (e, productId) => {
    if (e.key === 'ArrowDown' || (e.key === 'Enter' && !e.shiftKey)) {
      e.preventDefault();
      const id = `${type}-cost-${productId}`;
      const idx = qtyFocusOrder.indexOf(`${type}-qty-${productId}`);
      if (idx === -1) return;
      const nextQtyId = qtyFocusOrder[idx + 1];
      if (!nextQtyId) return;
      // Jump to next row's COST cell to mirror down-the-column flow
      const next = nextQtyId.replace(`${type}-qty-`, `${type}-cost-`);
      const el = document.querySelector(`[data-testid="${next}"]`);
      if (el) { el.focus(); el.select?.(); }
      // unused
      void id;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const idx = qtyFocusOrder.indexOf(`${type}-qty-${productId}`);
      if (idx <= 0) return;
      const prevQtyId = qtyFocusOrder[idx - 1];
      if (!prevQtyId) return;
      const prev = prevQtyId.replace(`${type}-qty-`, `${type}-cost-`);
      const el = document.querySelector(`[data-testid="${prev}"]`);
      if (el) { el.focus(); el.select?.(); }
    } else if (e.key === 'Tab' && !e.shiftKey) {
      // iter_postmix — in 'full' mode: Tab → total; otherwise: Tab → next qty
      if (getLineEditMode(productId) === 'full') {
        e.preventDefault();
        const el = document.querySelector(`[data-testid="${type}-total-${productId}"]`);
        if (el) { el.focus(); el.select?.(); }
      } else {
        e.preventDefault();
        focusQtyByOffset(productId, +1);
      }
    } else if (e.key === 'F6') {
      // iter_postmix — F6 toggles full line edit from cost field too
      e.preventDefault();
      setLineEditMode(productId, 'full');
    } else if (e.key === 'Escape') {
      // iter_postmix — Escape exits edit mode, returns to qty
      e.preventDefault();
      setLineEditMode(productId, 'none');
      const el = document.querySelector(`[data-testid="${type}-qty-${productId}"]`);
      if (el) { el.focus(); el.select?.(); }
    } else if (e.key === 'ArrowLeft') {
      const inp = e.currentTarget;
      if (inp.selectionStart === 0 && inp.selectionEnd === 0) {
        e.preventDefault();
        const el = document.querySelector(`[data-testid="${type}-qty-${productId}"]`);
        if (el) { el.focus(); el.select?.(); }
      }
    }
  };

  const linesTotal = (note.lines || []).reduce((s, l) => s + (parseFloat(l.line_total) || 0), 0);
  const linesWithQty = (note.lines || []).filter(l => l.quantity && parseFloat(l.quantity) > 0).length;

  // iter_postmix — Net / Gross totals for the modal
  const resolvedVatRate = vatRate || 20;
  const resolvedBasis = invoiceBasis || 'net';
  const netTotal = resolvedBasis === 'gross'
    ? linesTotal / (1 + resolvedVatRate / 100)
    : linesTotal;
  const grossTotal = resolvedBasis === 'net'
    ? linesTotal * (1 + resolvedVatRate / 100)
    : linesTotal;

  // iter363d — updateLine now keys off `beverage_product_id` instead
  // of a raw note.lines index so we can transparently promote an
  // augmented placeholder row into note.lines on first edit. If the
  // line isn't on note.lines yet (i.e. the auditor is typing on a
  // placeholder for a product they're adding), we append it.
  // iter363e — When the auditor enters a non-zero quantity AND the
  // placeholder carried a cost from the cascade, we copy the cost
  // into note.lines too so it gets saved server-side. The cost field
  // remains editable.
  const updateLine = (productId, field, val) => {
    const lines = [...(note.lines || [])];
    let idx = lines.findIndex(l => String(l.beverage_product_id) === String(productId));
    if (idx === -1) {
      const p = productMap[parseInt(productId)];
      const cascade = effectiveCostMap?.[String(productId)];
      const targetUnit = getDefaultUnit(p);
      // iter366 — rescale cascade cost into the target unit (matches
      // the placeholder-row build above).
      const cost = convertCascadeCost(cascade, targetUnit, p, type === 'delivery');
      lines.push({
        beverage_product_id: String(productId),
        product_name: p?.product_name || '',
        product_code: p?.product_code || '',
        quantity: '',
        cost_price: cost,
        cost_source: cascade?.source || null,
        cost_delivery_date: cascade?.delivery_date || null,
        cost_delivery_ref: cascade?.reference_number || null,
        cost_delivery_status: cascade?.delivery_status || null,
        unit_label: targetUnit,
        line_total: 0,
      });
      idx = lines.length - 1;
    }
    const line = { ...lines[idx] };
    // iter363f — Case ↔ bottle cost auto-conversion for CREDIT notes.
    // When the auditor toggles unit_label on a line that already
    // carries a cost_price, scale the cost by the ratio of the two
    // units' `conversion_to_base` so the per-unit cost stays correct
    // in base terms. E.g. a £12 case (12 bottles × 330 ml) toggled to
    // "bottle" becomes £1.
    // iter825 — Delivery notes NEVER auto-convert across units (a
    // different unit is an "incompatible unit" per spec): instead,
    // re-look-up the strict dnCostMap entry for the NEW unit; if it
    // doesn't match that exact unit, the cost is cleared blank.
    if (field === 'unit_label' && val && val !== line.unit_label) {
      if (type === 'delivery') {
        const cascade = effectiveCostMap?.[String(productId)];
        const matches = cascade?.cost_price != null && cascade.cost_price > 0
          && String(cascade.unit_label || '').trim().toLowerCase() === String(val).trim().toLowerCase();
        line.cost_price = matches ? String(cascade.cost_price) : '';
        line.cost_source = matches ? cascade.source : null;
        line.cost_delivery_date = matches ? cascade.delivery_date : null;
        line.cost_delivery_ref = matches ? cascade.reference_number : null;
        line.cost_delivery_status = matches ? cascade.delivery_status : null;
      } else {
        const prod = productMap[parseInt(productId)];
        const oldUnit = prod?.units?.find(u => u.unit_type === line.unit_label);
        const newUnit = prod?.units?.find(u => u.unit_type === val);
        const oldConv = parseFloat(oldUnit?.conversion_to_base);
        const newConv = parseFloat(newUnit?.conversion_to_base);
        const oldCost = parseFloat(line.cost_price);
        if (oldCost > 0 && oldConv > 0 && newConv > 0) {
          const newCost = oldCost * (newConv / oldConv);
          // Round to 4 dp (frontend display rounds to 2 dp anyway)
          line.cost_price = String(Math.round(newCost * 10000) / 10000);
          line.cost_source = 'unit_converted';
        }
      }
    }
    line[field] = val;
    // Auto-compute line_total = qty * cost_price
    if (field === 'quantity' || field === 'cost_price') {
      const qty = parseFloat(field === 'quantity' ? val : line.quantity) || 0;
      const cost = parseFloat(field === 'cost_price' ? val : line.cost_price) || 0;
      line.line_total = qty > 0 && cost > 0 ? Math.round(qty * cost * 100) / 100 : 0;
    }
    // iter426 (Iteration B #3) — Reverse direction: when the auditor
    // enters the line total, back-calculate the unit cost using the
    // current quantity (or 1 if qty isn't set yet — they're typically
    // entered before the total anyway).
    if (field === 'line_total') {
      const total = parseFloat(val) || 0;
      const qty = parseFloat(line.quantity) || 0;
      if (total > 0 && qty > 0) {
        const newCost = Math.round((total / qty) * 10000) / 10000;
        line.cost_price = String(newCost);
        line.cost_source = 'derived_from_total';
      }
      line.line_total = total > 0 ? Math.round(total * 100) / 100 : 0;
    }
    lines[idx] = line;
    userEditedRef.current = true;  // iter363 — gate autosave
    onUpdate({ ...note, lines });
  };

  // iter363e — Effect: when the auditor opens a NEW delivery note,
  // focus the first Qty input automatically. Triggered by the
  // `_new` flag + first non-empty `qtyFocusOrder` after products load.
  // iter_postmix fix: guard with autoFocusedRef so search-term changes
  // (which change qtyFocusOrder.length) don't steal focus from the search box.
  useEffect(() => {
    if (!note._new || !expanded) return;
    if (autoFocusedRef.current) return;  // already focused this note
    if (!qtyFocusOrder || qtyFocusOrder.length === 0) return;
    autoFocusedRef.current = true;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="${qtyFocusOrder[0]}"]`);
      if (el && typeof el.focus === 'function') el.focus();
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note._new, expanded, qtyFocusOrder.length]);

  const updateHeader = (field, val) => {
    userEditedRef.current = true;  // iter363 — gate autosave
    onUpdate({ ...note, [field]: val });
  };

  // iter363 — Debounced silent autosave for EXISTING notes only.
  // ONLY runs after a real user edit (gated by `userEditedRef`) so
  // parent re-renders that re-spread the `note` prop don't trigger
  // a save/reload loop. Also skips `onSaved`/loadData on success so
  // the panel doesn't flash through its "Loading deliveries…" state
  // after every keystroke — the in-memory state is already correct.
  //
  // 2026-11 fix — a background promotion (heartbeat/visibility-change,
  // via the panel-level `persistNewNote`) already saves whatever this
  // note's `lines`/header looked like at that moment, but it never
  // touches this CHILD-local `userEditedRef` (that save path doesn't go
  // through this component at all). Without resetting it here, the flag
  // stays stuck at whatever the user's own typing last set it to, and
  // the very first time this effect runs for the newly-promoted note
  // (note.id just became truthy) it fires an immediately-redundant
  // extra save of data that was already just persisted.
  const prevNoteIdRef = useRef(note.id);
  useEffect(() => {
    if (!prevNoteIdRef.current && note.id) {
      userEditedRef.current = false;
    }
    prevNoteIdRef.current = note.id;
  }, [note.id]);
  useEffect(() => {
    if (isOfflineMode || !isEditable) return;
    if (!note.id || note._new) return; // new notes saved by parent.save()
    if (!userEditedRef.current) return; // not a user edit — bail
    const hasValidLines = (note.lines || []).some(
      l => l.beverage_product_id && parseFloat(l.quantity) > 0
    );
    if (!hasValidLines) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      // eslint-disable-next-line no-use-before-define
      handleSave(/* silent */ true)
        .then(() => { userEditedRef.current = false; })
        .catch(() => { userEditedRef.current = false; });
    }, 1500);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note, isOfflineMode, isEditable]);

  // Save to server
  const handleSave = async (silent = false) => {
    if (isOfflineMode || !isEditable) return;
    if (saving) return;  // iter362h — guard against overlapping autosaves
    // iter426 — Hard block out-of-period deliveries. Silent autosaves
    // simply abort; explicit saves toast the reason so the auditor
    // knows what to fix.
    if (dateOutOfPeriod) {
      if (!silent) {
        toast({
          title: `${type === 'delivery' ? 'Delivery' : 'Credit'} date is outside the stocktake period`,
          description: `The note date must be between ${formatDDMMYYYY(periodFrom)} and ${formatDDMMYYYY(periodTo)}. Adjust the date and try again.`,
          variant: 'destructive',
        });
      }
      if (silent) setAutosaveState('idle');
      return;
    }
    setSaving(true);
    if (onSaveStart) onSaveStart();  // iter363f #8a — panel-level tracker
    if (silent) setAutosaveState('saving');    try {
      const validLines = (note.lines || []).filter(l => l.beverage_product_id && parseFloat(l.quantity) > 0);
      if (validLines.length === 0) {
        if (!silent) toast({ title: 'No items', description: 'Add quantities to at least one product', variant: 'destructive' });
        setSaving(false);
        if (onSaveEnd) onSaveEnd();
        if (silent) setAutosaveState('idle');
        return;
      }
      const service = type === 'delivery' ? deliveriesService : creditsService;
      // Build line payload with resolved unit_id (Bug fix 2026-04-20 —
      // label-only matching silently dropped edits when the stored
      // label had drifted).
      const linePayload = validLines.map(line => {
        const prod = products.find(p => p.id === parseInt(line.beverage_product_id));
        const resolvedUnit = prod?.units?.find(
          u => (u.unit_type || '').trim().toLowerCase() === (line.unit_label || '').trim().toLowerCase()
        );
        return {
          beverage_product_id: parseInt(line.beverage_product_id),
          quantity: parseFloat(line.quantity),
          cost_price: parseFloat(line.cost_price) || 0,
          unit_label: line.unit_label || null,
          unit_id: resolvedUnit ? resolvedUnit.id : null,
        };
      });

      const headerPayload = type === 'delivery'
        ? {
            supplier_id: note.supplier_id ? parseInt(note.supplier_id) : null,
            reference_number: note.reference_number || null,
            delivery_date: note.date || undefined,
            notes: note.notes || null,
            session_id: sessionId || undefined,
          }
        : {
            supplier_id: note.supplier_id ? parseInt(note.supplier_id) : null,
            reference_number: note.reference_number || null,
            credit_date: note.date || undefined,
            notes: note.notes || null,
            session_id: sessionId || undefined,
          };

      if (note.id && !note._new) {
        // ── UPDATE EXISTING NOTE (+ explicit post if still a draft) ──
        // Bug fix (2026-04-21): the old code just toasted "Saved"
        // without calling any API — edits on posted/existing notes
        // looked successful to the user but never reached the
        // backend. Now we replace lines (PUT /lines) and push header
        // changes via PUT /{id}. Works on DRAFT and POSTED notes
        // (backend accepts both; the stock_audit engine self-heals
        // stored stock_quantity on the next recalc).
        await service.update(note.id, headerPayload);
        await service.saveLines(note.id, linePayload);
        let justPosted = false;
        // 2026-11 fix (4th pass) — a background save (tab-hidden flush,
        // 60s heartbeat) only ever creates a DRAFT; THIS explicit save
        // (Save & Close / Save Now) is the "explicit Post" the auditor
        // actually asked for, so it must post a note that's still a
        // draft. Silent (background) saves must never post.
        if (!silent && note.status !== 'posted') {
          try {
            await service.post(note.id);
            justPosted = true;
          } catch (postErr) {
            // 2026-11 fix — only swallow the specific "Already posted" c…326 chars truncated…        if (postErr?.response?.data?.detail !== 'Already posted') throw postErr;
          }
        }
        if (!silent) toast({ title: `${type === 'delivery' ? 'Delivery' : 'Credit'} ${justPosted ? 'posted' : 'updated'}` });
        if (silent) setAutosaveState('saved');
        // iter363 — Only reload on EXPLICIT saves. Silent autosaves
        // already keep the in-memory state authoritative; reloading
        // here would re-spread `note`, trip the autosave effect, and
        // create the "Loading deliveries…" flash loop the user
        // reported.
        if (onSaved && !silent) onSaved();
      } else {
        // ── CREATE NEW NOTE (+ auto-post) ────────────────────────
        // 2026-11 fix — routed through the panel-level `persistNewNote`
        // mutex instead of calling create()/saveLines()/post() inline.
        // A background save (tab-hidden flush, 60s heartbeat, or the
        // wizard's "Save & Next" loop) can already be mid-flight for
        // this exact note; racing a second create() used to either rely
        // on the backend's reference-number dedup (fine, no duplicate
        // ROW) followed by a second post() that legitimately 400'd
        // "Already posted" — confusing, since the note really had just
        // been saved. persistNewNote waits for that in-flight attempt
        // instead, then we push an update with whatever is in the form
        // RIGHT NOW so any edits made after the other save started are
        // never lost.
        const created = await persistNewNote(note);
        if (!created) throw new Error('Could not save — no valid lines');
        await service.update(created.id, headerPayload);
        await service.saveLines(created.id, linePayload);
        if (!silent) toast({ title: `${type === 'delivery' ? 'Delivery' : 'Credit'} saved & posted` });
        if (silent) setAutosaveState('saved');
        // iter411 — Forward the persisted key/id; handleNoteSaved's own
        // bookkeeping is now a harmless no-op most of the time since
        // persistNewNote already did it, but stays as a safety net.
        if (onSaved) onSaved({ persistedKey: note._key, persistedId: created.id });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.error === 'cross_session_duplicate') {
        if (!silent) {
          toast({
            title: 'Duplicate Delivery Blocked',
            description: detail.message || `Reference '${note.reference_number}' was already posted for this site in a previous stocktake. This invoice cannot be counted again.`,
            variant: 'destructive',
            duration: 10000,
          });
        }
      } else if (!silent) {
        toast({ title: 'Save failed', description: getApiError(err, 'Error'), variant: 'destructive' });
      }
      if (silent) setAutosaveState('idle');
    } finally { setSaving(false); if (onSaveEnd) onSaveEnd(); }
  };

  // iter372 — Force-save (skip debounce). Tracks elapsed ms and shows
  // a confirmation toast with timing so the auditor knows the note is
  // safely persisted before moving on.
  const handleForceSave = async () => {
    if (saving) return;
    // Cancel any pending autosave timer to avoid a double-send.
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }
    const started = performance.now();
    try {
      await handleSave(/* silent */ false);
      const elapsed = Math.round(performance.now() - started);
      const ref = note.reference_number || note.supplier_name || (type === 'delivery' ? 'Delivery' : 'Credit');
      toast({
        title: `Saved · ${ref}`,
        description: `Persisted in ${elapsed}ms — safe to continue.`,
      });
      userEditedRef.current = false;
    } catch {/* handleSave already toasted the error */}
  };

  // Save & Close — explicit save from inside the modal, then dismiss.
  // Stays open if save fails so the auditor can fix the error.
  const handleSaveAndClose = async () => {
    if (saving) return;
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }
    let saved = false;
    try {
      await handleSave(/* silent */ false);
      saved = true;
      userEditedRef.current = false;
    } catch {/* handleSave already toasted the error */}
    if (saved) setExpanded(false);
  };

  // iter363f — function declaration so it's hoisted to the top of
  // the component scope (the mergedLines useMemo above references it
  // before this physical line).
  // Resolution order for delivery defaults (per user spec #1b):
  //   1. is_default_purchase_unit — set per-product in Master
  //      Inventory (e.g. "case" for packaged beer)
  //   2. is_base_stock_unit       — canonical internal unit
  //   3. first non-case unit      — historical fallback
  //   4. first declared unit
  function getDefaultUnit(prod) {
    if (!prod?.units?.length) return '';
    const purchaseUnit = prod.units.find(u => u.is_default_purchase_unit);
    if (purchaseUnit) return purchaseUnit.unit_type;
    const stockUnit = prod.units.find(u => u.is_base_stock_unit);
    if (stockUnit) return stockUnit.unit_type;
    const singles = prod.units.find(u => u.unit_type !== 'case');
    if (singles) return singles.unit_type;
    return prod.units[0].unit_type;
  }

  // iter_postmix — keydown handler for Total input in 'full' line-edit mode
  const handleTotalKeyDown = (e, productId) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      // Tab from Total → next qty (exit full-line edit for this row)
      setLineEditMode(productId, 'none');
      focusQtyByOffset(productId, +1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setLineEditMode(productId, 'none');
      const el = document.querySelector(`[data-testid="${type}-qty-${productId}"]`);
      if (el) { el.focus(); el.select?.(); }
    }
  };

  // Totals bar component for reuse at top and bottom of modal
  const TotalsBar = ({ compact }) => (
    <div className={`flex items-center gap-4 px-4 py-2 bg-slate-50 border-b border-slate-200 ${compact ? 'text-xs' : 'text-sm'}`} data-testid={`${type}-totals-bar-${note._key || note.id}`}>
      <div className="flex-1 flex items-center gap-6">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs">Items:</span>
          <span className="font-semibold">{linesWithQty}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs font-medium">Net (ex-VAT):</span>
          <span className="font-bold text-slate-800 tabular-nums" data-testid={`${type}-net-total-${note._key || note.id}`}>{fmt(netTotal)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs font-medium">Gross (inc-VAT):</span>
          <span className="font-bold text-blue-700 tabular-nums" data-testid={`${type}-gross-total-${note._key || note.id}`}>{fmt(grossTotal)}</span>
        </div>
        {linesWithQty === 0 && <span className="text-xs text-muted-foreground italic">No quantities entered yet</span>}
      </div>
      {isEditable && !isOfflineMode && note.id && !note._new && (
        <span className={`text-[11px] shrink-0 ${autosaveState === 'saving' ? 'text-amber-600' : autosaveState === 'saved' ? 'text-emerald-600' : 'text-muted-foreground'}`}>
          {autosaveState === 'saving' && <><Loader2 className="h-3 w-3 animate-spin inline mr-1" />{note.status === 'posted' ? 'Saving…' : 'Saving (still draft)…'}</>}
          {autosaveState === 'saved' && (note.status === 'posted' ? '✓ Saved' : '✓ Saved — still a draft, not posted')}
          {autosaveState === 'idle' && (note.status === 'posted' ? '· edits save automatically' : '· edits save automatically — this note is still a draft, not posted')}
        </span>
      )}
    </div>
  );

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`${type}-note-${note._key || note.id}`}>
      {/* Header — always visible, click to open modal */}
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium">
            {note.supplier_name || note.reference_number || `New ${type === 'delivery' ? 'Delivery' : 'Credit Note'}`}
            {note.reference_number && note.supplier_name ? ` — ${note.reference_number}` : ''}
          </span>
          <span className="text-xs text-muted-foreground ml-2">
            {formatDDMMYYYY(note.date)} | {linesWithQty} items | {fmt(linesTotal)}
          </span>
        </div>
        {/* D7 — Inconsistency badge: shown when header total ≠ live line sum (duplicate lines). */}
        {note.lines_consistent === false && (
          <Badge variant="outline" className="text-[9px] bg-red-50 text-red-700 border-red-300 shrink-0" title={`Header £${(note.lines_total||0).toFixed(2)} ≠ lines £${(note.lines_actual_sum||0).toFixed(2)} — possible duplicate line`}>
            Line mismatch
          </Badge>
        )}
        {note.status && (
          <Badge variant="outline" className={`text-[9px] ${note._new ? 'bg-slate-100 text-slate-600 border-slate-300' : note.status === 'posted' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}
            title={note._new ? 'Not yet saved to the server — use Save & Close to post now, or it saves as a draft within about a minute (posting still needs an explicit Save & Close or Post All & Calculate)' : note.status === 'posted' ? 'Saved and posted — included in the calculation' : 'Saved but not posted — excluded from the calculation until posted'}>
            {note._new ? 'Not saved yet' : note.status === 'posted' ? 'Posted' : 'Draft (unposted)'}
          </Badge>
        )}
        {isEditable && onDelete && (
          <Button
            size="sm" variant="outline" className="h-7 px-2 text-[11px] shrink-0"
            onClick={(e) => { e.stopPropagation(); handleForceSave(); }}
            disabled={saving || isOfflineMode}
            data-testid={`save-note-${note._key || note.id}`}>
            {saving ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Saving…</> : <><Save className="h-3 w-3 mr-1" />Save now</>}
          </Button>
        )}
        {isEditable && onDelete && (
          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600 shrink-0"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* ── Delivery/Credit Note pop-up modal (iter_postmix UX) ── */}
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent
          ref={dialogContentRef}
          className="flex flex-col p-0 gap-0 overflow-hidden rounded-lg shadow-xl"
          style={{
            width: modalSize ? `${modalSize.width}px` : 'min(1440px, 96vw)',
            height: modalSize ? `${modalSize.height}px` : '93vh',
            minWidth: '700px',
            minHeight: '480px',
            maxWidth: '99vw',
            maxHeight: '99vh',
            resize: 'both',
          }}
          data-testid={`${type}-modal-${note._key || note.id}`}
        >
          <DialogHeader className="px-4 pt-4 pb-2 border-b bg-white shrink-0">
            <div className="flex items-center justify-between gap-3">
              <DialogTitle className="text-base font-semibold">
                {type === 'delivery' ? 'Delivery Note' : 'Credit Note'}
                {note.supplier_name && <span className="text-muted-foreground font-normal ml-2">· {note.supplier_name}</span>}
                {note.reference_number && <span className="text-muted-foreground font-normal ml-1">· {note.reference_number}</span>}
              </DialogTitle>
              {note.status && (
                <Badge variant="outline" className={`text-[10px] mr-6 ${note._new ? 'bg-slate-100 text-slate-600 border-slate-300' : note.status === 'posted' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}
                  title={note._new ? 'Not yet saved to the server — use Save & Close to post now, or it saves as a draft within about a minute (posting still needs an explicit Save & Close or Post All & Calculate)' : note.status === 'posted' ? 'Saved and posted — included in the calculation' : 'Saved but not posted — excluded from the calculation until posted'}>
                  {note._new ? 'Not saved yet' : note.status === 'posted' ? 'Posted' : 'Draft (unposted)'}
                </Badge>
              )}
            </div>
            {/* Keyboard shortcut hint */}
            <p className="text-[10px] text-muted-foreground mt-1">
              <kbd className="bg-slate-100 px-1 rounded text-[9px]">Tab</kbd> moves between qty fields &nbsp;·&nbsp;
              <kbd className="bg-slate-100 px-1 rounded text-[9px]">F4</kbd> edit cost price &nbsp;·&nbsp;
              <kbd className="bg-slate-100 px-1 rounded text-[9px]">F6</kbd> full line edit &nbsp;·&nbsp;
              <kbd className="bg-slate-100 px-1 rounded text-[9px]">Esc</kbd> return to qty
            </p>

            {/* Search + Category/Subcategory pills + Add to Inventory — pinned in header */}
            <div className="mt-2 space-y-2">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search products..."
                    className="pl-8 h-8 text-sm"
                    autoComplete="off" autoCorrect="off" spellCheck={false}
                    name="wizard-deliveries-search-input"
                    data-testid={`${type}-search-input`}
                  />
                </div>
                {/* Add Product From Inventory Master button — opens nested modal,
                    delivery note stays open, data is preserved */}
                {isEditable && !isOfflineMode && onProductAdded && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0 h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => setNoteAddFromMasterOpen(true)}
                    data-testid={`${type}-add-from-master-btn`}
                    title="Add a product from the inventory master to this site, then enter its delivery quantity"
                  >
                    <PackagePlus className="h-3.5 w-3.5 mr-1" />
                    Add to Inventory
                  </Button>
                )}
              </div>
              {(availableCategories.length > 0 || availableSubcategories.length > 0) && (
                <div className="space-y-1.5 text-xs">
                  {availableCategories.length > 0 && (
                    <div className="flex flex-wrap gap-1 items-center">
                      <span className="text-[10px] font-medium text-slate-400 shrink-0 mr-1">Cat:</span>
                      {availableCategories.map(cat => (
                        <button key={cat}
                          onClick={() => setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat])}
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                            selectedCategories.includes(cat) ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-emerald-400'
                          }`}>{cat.replace(/_/g, ' ')}</button>
                      ))}
                    </div>
                  )}
                  {availableSubcategories.length > 0 && (
                    <div className="flex flex-wrap gap-1 items-center">
                      <span className="text-[10px] font-medium text-slate-400 shrink-0 mr-1">Sub:</span>
                      {availableSubcategories.map(sub => (
                        <button key={sub}
                          onClick={() => setSelectedSubcategories(prev => prev.includes(sub) ? prev.filter(s => s !== sub) : [...prev, sub])}
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                            selectedSubcategories.includes(sub) ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-emerald-400'
                          }`}>{sub}</button>
                      ))}
                    </div>
                  )}
                  {activeFilterCount > 0 && (
                    <button onClick={() => { setSelectedCategories([]); setSelectedSubcategories([]); }}
                      className="text-[10px] text-slate-500 hover:text-slate-700 flex items-center gap-0.5">
                      <X className="w-2.5 h-2.5" /> Clear filters ({activeFilterCount})
                    </button>
                  )}
                </div>
              )}
            </div>
          </DialogHeader>

          {/* Totals — TOP */}
          <TotalsBar compact={false} />

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 space-y-3">
              {/* Header fields */}
              {isEditable && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Supplier</Label>
                    <div className="flex items-center gap-1">
                      <Select value={note.supplier_id || 'none'} onValueChange={v => updateHeader('supplier_id', v === 'none' ? '' : v)}>
                        <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Optional" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {allSuppliers.map(s => <SelectItem key={s.id} value={String(s.id)}>{s.supplier_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button
                        type="button"
                        title="Add new supplier"
                        onClick={() => setQuickAddSupplierOpen(true)}
                        className="h-8 w-8 shrink-0 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:border-emerald-400 hover:text-emerald-600 transition-colors"
                        data-testid="delivery-note-add-supplier-btn"
                      >
                        +
                      </button>
                    </div>
                    <QuickAddSupplierModal
                      open={quickAddSupplierOpen}
                      onOpenChange={setQuickAddSupplierOpen}
                      onCreated={handleQuickSupplierCreated}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Reference</Label>
                    <Input className="h-8 text-xs" placeholder="Invoice/Ref #" value={note.reference_number || ''} onChange={e => updateHeader('reference_number', e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Date</Label>
                    <Input
                      type="date"
                      className={`h-8 text-xs ${dateOutOfPeriod ? 'border-red-400 bg-red-50' : ''}`}
                      value={note.date || ''}
                      min={periodFrom || undefined}
                      max={periodTo || undefined}
                      onChange={e => updateHeader('date', e.target.value)}
                      data-testid={`${type}-date-input`}
                    />
                    {dateOutOfPeriod && (
                      <p className="text-[10px] text-red-600 font-medium" data-testid={`${type}-date-out-of-period`}>
                        Date must be between {formatDDMMYYYY(periodFrom)} and {formatDDMMYYYY(periodTo)}.
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Notes</Label>
                    <Input className="h-8 text-xs" placeholder="Optional" value={note.notes || ''} onChange={e => updateHeader('notes', e.target.value)} />
                  </div>
                </div>
              )}

              {/* Column headers */}
              <div className="text-xs text-muted-foreground font-semibold px-1 flex gap-2">
                <div className="flex-[2]">Product</div>
                <div className="w-24">Unit</div>
                <div className="w-20">Qty</div>
                <div className="w-28">Cost Price <span className="text-[9px] font-normal opacity-60">(F4)</span></div>
                <div className="w-24 text-right pr-1">Total <span className="text-[9px] font-normal opacity-60">(F6)</span></div>
              </div>

              {/* Product rows grouped by subcategory */}
              <div className="space-y-0.5" data-testid={`${type}-lines-container`}>
                {(() => {
                  let rendered = 0;
                  const blocks = [];
                  for (const group of groupedVisibleLines) {
                    if (rendered >= renderLimit) break;
                    const groupRows = [];
                    for (const line of group.lines) {
                      if (rendered >= renderLimit) break;
                      groupRows.push(line);
                      rendered += 1;
                    }
                    if (groupRows.length === 0) continue;
                    blocks.push({ subcategory: group.subcategory, totalCount: group.lines.length, lines: groupRows });
                  }
                  return blocks.map(block => (
                    <div key={`grp-${block.subcategory}`} data-testid={`${type}-subcategory-group-${block.subcategory.replace(/\s+/g, '-').toLowerCase()}`}>
                      <div className="sticky top-0 z-[1] bg-slate-100/95 backdrop-blur px-2 py-1 border-y border-slate-200 mb-1 rounded-sm">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                          {block.subcategory}
                          <span className="ml-2 text-slate-400 normal-case font-normal">({block.totalCount})</span>
                        </p>
                      </div>
                      {block.lines.map(line => {
                        const idx = line._origIdx;
                        const pid = line.beverage_product_id;
                        const prod = productMap[parseInt(pid)];
                        const hasQty = line.quantity && parseFloat(line.quantity) > 0;
                        const editMode = getLineEditMode(pid);
                        const costReadOnly = isEditable && editMode === 'none';
                        const totalReadOnly = isEditable && editMode !== 'full';
                        const unitOptions = prod?.units?.length > 0
                          ? prod.units.map(u => ({ value: u.unit_type, label: u.unit_type.charAt(0).toUpperCase() + u.unit_type.slice(1) }))
                          : [];
                        // iter_postmix — improved cost source badge with date
                        // iter825 — delivery notes now source exclusively from
                        // dn_line (same site, same purchase unit); credit notes
                        // keep the existing last_delivery/opening_stock badges.
                        const cascadeDate = line.cost_delivery_date ? formatDDMMYYYY(line.cost_delivery_date) : null;
                        const costSourceBadge = line.cost_source === 'dn_line'
                          ? {
                              label: 'DN',
                              title: `Cost from DN${line.cost_delivery_ref ? ` #${line.cost_delivery_ref}` : ''}${cascadeDate ? ` on ${cascadeDate}` : ''}${line.cost_delivery_status ? ` (${line.cost_delivery_status})` : ''} — this site`,
                            }
                          : (line.cost_source === 'last_delivery' || line.cost_source === 'last_delivery_supplier')
                          ? {
                              label: 'L',
                              title: `Cost from last delivery${cascadeDate ? ` (${cascadeDate})` : ''}${line.cost_source === 'last_delivery_supplier' ? ' — this supplier' : ' — this site'}`,
                            }
                          : line.cost_source === 'opening_stock'
                            ? { label: 'O', title: 'Cost from opening stock' }
                            : null;
                        return (
                          <div key={pid || idx}
                            className={`flex gap-2 items-center py-1 ${hasQty ? 'bg-emerald-50/60 rounded px-1' : ''}`}
                            data-testid={`${type}-line-${pid}`}>
                            <div className="flex-[2] min-w-0">
                              <span className="text-sm truncate block">
                                {prod?.product_code && <span className="text-slate-400 text-xs">{prod.product_code} </span>}
                                {prod?.product_name || line.product_name || '?'}
                              </span>
                              {prod?.default_unit_hint && (
                                <span className="block text-[9px] text-slate-400 italic truncate leading-tight" data-testid={`${type}-unit-hint-${pid}`}>
                                  {prod.default_unit_hint}
                                </span>
                              )}
                            </div>
                            <div className="w-24">
                              {unitOptions.length > 0 ? (
                                <Select value={line.unit_label || 'none'} onValueChange={v => updateLine(pid, 'unit_label', v === 'none' ? '' : v)} disabled={!isEditable}>
                                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="--" /></SelectTrigger>
                                  <SelectContent>{unitOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                                </Select>
                              ) : (
                                <span className="text-xs text-muted-foreground">{line.unit_label || '--'}</span>
                              )}
                            </div>
                            <div className="w-20">
                              <Input type="number" step="0.01" min="0" placeholder="--" inputMode="decimal"
                                value={line.quantity || ''} onChange={e => updateLine(pid, 'quantity', e.target.value)}
                                onKeyDown={e => handleQtyKeyDown(e, pid)}
                                disabled={!isEditable} className="h-8 text-sm"
                                data-testid={`${type}-qty-${pid}`} />
                            </div>
                            <div className="w-28 relative">
                              <Input type="number" step="0.01" min="0" placeholder="Cost" inputMode="decimal"
                                value={line.cost_price || ''} onChange={e => updateLine(pid, 'cost_price', e.target.value)}
                                onKeyDown={e => handleCostKeyDown(e, pid)}
                                onClick={() => { if (isEditable && editMode === 'none') setLineEditMode(pid, 'cost'); }}
                                readOnly={costReadOnly}
                                disabled={!isEditable}
                                className={`h-8 text-sm ${costSourceBadge ? 'pr-7' : ''} ${costReadOnly ? 'bg-slate-50 text-slate-400 cursor-pointer' : ''}`}
                                title={costReadOnly ? 'Click or press F4 to edit cost price' : undefined}
                                data-testid={`${type}-cost-${pid}`} />
                              {costSourceBadge && (
                                <span
                                  className={`absolute right-1 top-1/2 -translate-y-1/2 text-[9px] font-mono rounded px-1 leading-tight ${line.cost_source === 'dn_line' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}
                                  title={costSourceBadge.title}
                                  data-testid={`${type}-cost-source-badge-${pid}`}
                                >{costSourceBadge.label}</span>
                              )}
                            </div>
                            <div className="w-24">
                              <Input type="number" step="0.01" min="0" placeholder="Total" inputMode="decimal"
                                value={hasQty && line.line_total ? line.line_total : ''}
                                onChange={e => updateLine(pid, 'line_total', e.target.value)}
                                onKeyDown={e => handleTotalKeyDown(e, pid)}
                                onClick={() => { if (isEditable && editMode !== 'full') setLineEditMode(pid, 'full'); }}
                                readOnly={totalReadOnly}
                                disabled={!isEditable}
                                className={`h-8 text-sm text-right ${totalReadOnly ? 'bg-slate-50 text-slate-400 cursor-pointer' : ''}`}
                                title={totalReadOnly ? 'Click or press F6 to edit total' : undefined}
                                data-testid={`${type}-total-${pid}`} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}
                {visibleLines.length > renderLimit && (
                  <div className="pt-1 text-center">
                    <button className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => setRenderLimit(prev => prev + 200)}>
                      Showing {renderLimit} of {visibleLines.length} — show more
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Totals — BOTTOM */}
          <TotalsBar compact={false} />

          {/* Modal footer — explicit Save & Close (Item 6 fix) */}
          <div className="shrink-0 flex items-center justify-between px-4 py-2.5 border-t bg-white gap-3" data-testid={`${type}-modal-footer`}>
            {dateOutOfPeriod ? (
              <p className="text-xs text-red-600 font-medium flex-1">
                Date outside period — adjust between {formatDDMMYYYY(periodFrom)} and {formatDDMMYYYY(periodTo)}.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground flex-1">
                {autosaveState === 'saving' && <><Loader2 className="h-3 w-3 animate-spin inline mr-1" />{note._new ? 'Saving & posting…' : note.status === 'posted' ? 'Saving…' : 'Saving (still draft)…'}</>}
                {autosaveState === 'saved' && (note._new ? '✓ Saved & posted' : note.status === 'posted' ? '✓ Saved' : '✓ Saved — still a draft, not posted')}
                {autosaveState === 'idle' && (isEditable && !isOfflineMode
                  ? (note._new
                    ? 'Click Save & Close to post now — unposted lines also save as a draft within about a minute (posting still needs Save & Close or Post All & Calculate)'
                    : (note.status === 'posted' ? 'Changes save automatically' : 'Changes save automatically — this note is still a draft, not posted'))
                  : '')}
              </p>
            )}
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpanded(false)}
                data-testid={`${type}-modal-close-btn`}
              >
                Close
              </Button>
              {isEditable && !isOfflineMode && (
                <Button
                  size="sm"
                  onClick={handleSaveAndClose}
                  disabled={saving || dateOutOfPeriod}
                  data-testid={`${type}-modal-save-close-btn`}
                >
                  {saving
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Saving…</>
                    : <><Save className="h-3.5 w-3.5 mr-1" />Save & Close</>}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Nested "Add Product From Inventory Master" modal — opens on top
          of this delivery note dialog without closing it. After the user
          assigns products, onProductAdded() asks the parent to reload
          its siteFilteredProducts; the new rows then appear in this
          note's mergedLines automatically. */}
      {noteAddFromMasterOpen && (
        <AddFromMasterModal
          open={noteAddFromMasterOpen}
          onOpenChange={setNoteAddFromMasterOpen}
          siteId={siteId}
          assignedProductIds={assignedProductIds || []}
          onAssigned={() => {
            if (onProductAdded) onProductAdded();
          }}
        />
      )}
    </div>
  );
}


// ── Main Panel ──
const WizardDeliveriesCreditsPanel = forwardRef(function WizardDeliveriesCreditsPanel({ siteId, sessionId, periodFrom, periodTo, isEditable, offlineData, offlineLocalChanges, isOfflineMode, onOfflineDeliveriesChange }, ref) {
  const [activeTab, setActiveTab] = useState('delivery');
  const [deliveries, setDeliveries] = useState([]);
  const [credits, setCredits] = useState([]);
  const [newNotes, setNewNotes] = useState([]); // In-progress new delivery/credit notes
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [siteProductIds, setSiteProductIds] = useState(null);
  // iter363e — Bulk delivery cost cascade for the site (last delivery
  // → opening stock → 0). Drives auto-population of `cost_price`
  // when the auditor enters a quantity on a new delivery line; the
  // value can still be edited by hand.
  const [costMap, setCostMap] = useState({});
  // iter825 — strict same-site/same-unit/DN-line-only cost default for
  // Delivery Notes specifically (see /deliveries/dn-site-cost-map).
  // Credit notes keep using `costMap` above unchanged.
  const [dnCostMap, setDnCostMap] = useState({});
  const [dataSource, setDataSource] = useState(null);
  const { toast } = useToast();
  const autoSaveRef = useRef(null);
  // newNotesRef — keeps a current snapshot of newNotes accessible
  // in event handlers (visibilitychange) without stale closure issues.
  const newNotesRef = useRef([]);
  // iter363 — Concurrency lock for the imperative `save()` handle.
  // The wizard footer ("Save & Next") and step navigation buttons all
  // call `deliveriesRef.current.save()`. Without a guard, a quick
  // double-/triple-click captures the same `newNotes` closure each
  // time (state hasn't flushed yet) and POSTs the same note multiple
  // times — which is exactly what produced the triplicated delivery
  // notes 5206 / 0124 the user reported. We use a ref so concurrent
  // callers can detect an in-flight save without waiting on React
  // state to settle.
  const savingRef = useRef(false);
  // iter411 — Per-key persistence tracker for new (draft) notes. When
  // a child DeliveryNote auto-saves itself (via the "Save now" button),
  // we record its `_key` here so the parent's imperative `save()`
  // loop SKIPS it on the next wizard step navigation. This kills the
  // duplicate-delivery-note bug that Mercure Swansea triggered: the
  // child created server delivery A, and the wizard's parent loop
  // then re-POSTed the same in-memory draft (which still lived in
  // `newNotes` for a few ms after the child save) producing delivery
  // B with the same lines.
  // 2026-11 fix — upgraded from a Set to a Map (key -> delivery id) so the
  // id is always resolvable from this ref alone at any point — including
  // from a stale closure that only has the ORIGINAL note snapshot, not
  // the live, promoted one in `newNotes`. A plain Set only told callers
  // "yes, already saved" with no way to recover which server id that was.
  const persistedKeysRef = useRef(new Map());
  // iter595 — SessionStorage draft key for this session. Persists
  // unsaved new-note data across React Router navigations (sidebar,
  // browser back) so returning auditors see their work intact. Only
  // header fields + filled lines are stored (not the full empty product
  // catalog) to keep the payload lean.
  const draftKey = sessionId ? `abbey_delivery_draft_${sessionId}` : null;
  const draftRestoredRef = useRef(false);
  // iter363f #8a — Panel-level "save in progress" counter. Drives
  // (a) a visible banner in the wizard footer area, (b) a
  // beforeunload listener that warns the auditor before they close
  // the tab mid-save, and (c) the imperative `flushPending()` hook
  // used by the wizard footer to block step navigation until silent
  // autosaves on individual notes have completed.
  const [savingNotesCount, setSavingNotesCount] = useState(0);
  const savingNotesCountRef = useRef(0);

  // 2026-11 fix — single, mutex-protected path for "create + saveLines +
  // post a NEW note". This exact sequence used to be duplicated
  // independently in FOUR places (this child's own Save/autosave below,
  // the tab-hidden flush, the 60s heartbeat, and the wizard's
  // "Save & Next" loop) with no shared lock between them. Any two of
  // those racing within the several seconds a create→post round trip
  // takes here could both reach `post()` for the SAME note: the
  // backend's reference-number dedup on `create()` prevented a literal
  // duplicate delivery ROW, but the loser's own `post()` call then
  // legitimately 400'd "Already posted" — confusing, because the note
  // really had just been saved by the other path a moment earlier.
  // Separately, the ORIGINAL code "promoted" the note in place (set id,
  // left status:'draft' unchanged) — that stale status is what made a
  // note briefly look like both Draft and Posted once the authoritative
  // `deliveries`/`credits` list reloaded and showed the SAME delivery as
  // Posted. This version also promotes in place (set id, status:
  // 'posted', _new:false — so the existing per-note autosave effect takes
  // over any further edits instead of the component being unmounted
  // mid-edit), but `existingItems` further down now dedupes by id so the
  // promoted entry never renders twice once the reload catches up.
  // `persistedNoteSavesRef` is a per-key in-flight-promise registry: a
  // second caller for the same key awaits the FIRST caller's promise
  // instead of racing a second `create()` call.
  const persistedNoteSavesRef = useRef(new Map());
  // 2026-11 fix (4th pass) — background saves (tab-hidden flush, 60s
  // heartbeat) must create/save a brand-new note as a DRAFT only, never
  // post it: a reported bug showed a note flipping to Posted while the
  // auditor was still actively typing lines, with no explicit Save &
  // Close ever clicked. Explicit actions (Save & Close, Save Now, Save &
  // Next) still post immediately — `post` defaults to true for them.
  // `persistedPostedRef` tracks, per note key, whether THIS mutex has
  // already posted it, so a later explicit save on an already-draft note
  // calls post() exactly once instead of re-posting (which the backend
  // would reject as "Already posted").
  const persistedPostedRef = useRef(new Map());
  const persistNewNote = useCallback(async (note, { post = true } = {}) => {
    const key = note._key;
    if (key && persistedKeysRef.current.has(key)) {
      // Already created (as draft or posted) by an earlier call. A per-note
      // autosave effect normally catches up on any edit made AFTER
      // promotion within ~2s, but a caller (e.g. "Save & Next") can land
      // inside that window — push whatever is CURRENTLY on this note
      // now too, so nothing typed after promotion but before this call
      // is ever silently dropped.
      const id = persistedKeysRef.current.get(key);
      try {
        const service = note._type === 'delivery' ? deliveriesService : creditsService;
        const validLinesNow = (note.lines || []).filter(l => l.beverage_product_id && parseFloat(l.quantity) > 0);
        if (validLinesNow.length > 0) {
          const linePayloadNow = validLinesNow.map(line => {
            const prod = products.find(p => p.id === parseInt(line.beverage_product_id));
            const resolvedUnit = prod?.units?.find(
              u => (u.unit_type || '').trim().toLowerCase() === (line.unit_label || '').trim().toLowerCase()
            );
            return {
              beverage_product_id: parseInt(line.beverage_product_id),
              quantity: parseFloat(line.quantity),
              cost_price: parseFloat(line.cost_price) || 0,
              unit_label: line.unit_label || null,
              unit_id: resolvedUnit ? resolvedUnit.id : null,
            };
          });
          await service.saveLines(id, linePayloadNow);
        }
        // An earlier background save left this note as a DRAFT; THIS call
        // is an explicit one (post=true) catching it up to Posted.
        if (post && !persistedPostedRef.current.get(key)) {
          try {
            await service.post(id);
          } catch (postErr) {
            // Already posted by another path (e.g. Post All & Calculate) —
            // that is the terminal state we wanted anyway, not a failure.
            // 2026-11 fix — match the specific "Already posted" detail,
            // not just any 400; a genuine validation 400 (e.g. no lines)
            // must re-throw so the status below is never force-set to
            // 'posted' for a note that was never actually posted.
            if (postErr?.response?.data?.detail !== 'Already posted') throw postErr;
          }
          persistedPostedRef.current.set(key, true);
          setNewNotes(prev => prev.map(n => (n._key === key ? { ...n, status: 'posted' } : n)));
        }
      } catch {
        // Best-effort — the note is already safely created either way;
        // a later save attempt will retry this.
      }
      return { id };
    }
    if (key && persistedNoteSavesRef.current.has(key)) {
      // Another path is already creating/saving this exact note —
      // wait for THAT attempt instead of starting a duplicate one.
      return persistedNoteSavesRef.current.get(key);
    }
    const validLines = (note.lines || []).filter(l => l.beverage_product_id && parseFloat(l.quantity) > 0);
    if (validLines.length === 0) return null;
    const promise = (async () => {
      const service = note._type === 'delivery' ? deliveriesService : creditsService;
      const payload = note._type === 'delivery'
        ? { site_id: siteId, supplier_id: note.supplier_id ? parseInt(note.supplier_id) : null, reference_number: note.reference_number || null, delivery_date: note.date || periodTo || new Date().toISOString().split('T')[0], notes: note.notes || null, session_id: sessionId || undefined }
        : { site_id: siteId, supplier_id: note.supplier_id ? parseInt(note.supplier_id) : null, reference_number: note.reference_number || null, credit_date: note.date || periodTo || new Date().toISOString().split('T')[0], notes: note.notes || null, session_id: sessionId || undefined };
      const created = await service.create(payload);
      const linePayload = validLines.map(line => {
        const prod = products.find(p => p.id === parseInt(line.beverage_product_id));
        const resolvedUnit = prod?.units?.find(
          u => (u.unit_type || '').trim().toLowerCase() === (line.unit_label || '').trim().toLowerCase()
        );
        return {
          beverage_product_id: parseInt(line.beverage_product_id),
          quantity: parseFloat(line.quantity),
          cost_price: parseFloat(line.cost_price) || 0,
          unit_label: line.unit_label || null,
          unit_id: resolvedUnit ? resolvedUnit.id : null,
        };
      });
      await service.saveLines(created.id, linePayload);
      if (post) {
        await service.post(created.id);
        if (key) persistedPostedRef.current.set(key, true);
      }
      if (key) {
        persistedKeysRef.current.set(key, created.id);
        // 2026-11 fix (2nd pass) — PROMOTE in place rather than removing.
        // Removing it used to unmount the open modal mid-edit: any
        // keystrokes made WHILE this create→saveLines→post round trip was
        // in flight (several seconds on this backend) were on the LATEST
        // `newNotes` entry, which then vanished along with the component
        // that would have autosaved them — a genuine data-loss regression
        // found in testing. Promoting instead (id + _new:false, using the
        // functional updater so it merges onto whatever is CURRENTLY in
        // state, not this stale snapshot) hands the note straight to the
        // existing PER-NOTE autosave effect below (gated on
        // `note.id && !note._new`), which then saves any such late edits
        // itself, exactly like any other existing note. The
        // dedupe-by-id in `existingItems` (further down) is what stops
        // this promoted entry from ALSO rendering a second time once
        // `deliveries`/`credits` reloads and contains the same row.
        setNewNotes(prev => prev.map(n => (
          n._key === key ? { ...n, id: created.id, _new: false, status: post ? 'posted' : 'draft' } : n
        )));
      }
      return created;
    })();
    if (key) persistedNoteSavesRef.current.set(key, promise);
    try {
      return await promise;
    } finally {
      if (key) persistedNoteSavesRef.current.delete(key);
    }
  }, [siteId, sessionId, periodTo, products]);

  // iter_postmix — Load org VAT settings for net/gross totals display in DeliveryNote modal
  const [orgVatSettings, setOrgVatSettings] = useState({ default_vat_rate: 20, invoice_basis: 'net' });
  useEffect(() => {
    stocktakingSettingsService.get()
      .then(s => setOrgVatSettings({ default_vat_rate: s.default_vat_rate || 20, invoice_basis: s.invoice_basis || 'net' }))
      .catch(() => {});
  }, []);
  const startNoteSave = useCallback(() => {
    savingNotesCountRef.current += 1;
    setSavingNotesCount(n => n + 1);
  }, []);
  const endNoteSave = useCallback(() => {
    savingNotesCountRef.current = Math.max(0, savingNotesCountRef.current - 1);
    setSavingNotesCount(n => Math.max(0, n - 1));
  }, []);
  const isAnySaveInFlight = savingNotesCount > 0;

  // Keep newNotesRef in sync so the visibilitychange handler (below)
  // can read current notes without a stale closure.
  useEffect(() => { newNotesRef.current = newNotes; }, [newNotes]);

  // iter_data_loss_fix — Visibility-change auto-save. When the user
  // switches to another window (Teams, email, etc.) while a new delivery
  // note has data, immediately push it to the server. This is the last
  // line of defence before a tab discard or crash — localStorage already
  // covers those cases, but a server-side save means data survives even
  // if the local storage key is cleared.
  //
  // 2026-11 fix — routed through the shared `persistNewNote` mutex (see
  // above) instead of an inline create()/saveLines()/post() sequence.
  // `persistNewNote` now PROMOTES the note in place on success (keeps it
  // mounted so later edits aren't lost — see persistNewNote's own
  // comment) rather than removing it; `existingItems` dedupes by id so
  // it never renders twice once `deliveries`/`credits` catches up.
  const autoSavedWhileHiddenRef = useRef(false);
  useEffect(() => {
    if (isOfflineMode) return;
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // ── Tab hidden: push any new notes with data to the server ──
        const pending = (newNotesRef.current || []).filter(n => {
          const hasLines = (n.lines || []).some(l => parseFloat(l.quantity) > 0);
          const hasHeader = !!(n.supplier_id || n.reference_number || n.notes);
          return hasLines || hasHeader;
        });
        if (pending.length === 0) return;
        for (const note of pending) {
          if (note._key && persistedKeysRef.current.has(note._key)) continue;
          try {
            // 2026-11 fix (4th pass) — background (tab-hidden) flush saves
            // as a DRAFT only; it must never post a note the auditor is
            // still actively entering lines into.
            const created = await persistNewNote(note, { post: false });
            if (created) {
              // 2026-11 fix — if the tab became visible again WHILE this
              // save was still in flight, reload right now instead of
              // relying on `autoSavedWhileHiddenRef` + a future
              // visibilitychange event that may never fire again if the
              // user doesn't blur the tab a second time. `silent=true` —
              // see loadData's own comment: a NON-silent reload here
              // flips the page to "Loading deliveries…" and unmounts
              // every open modal, which is what was actually discarding
              // an edit made during this background round trip.
              if (document.hidden) {
                autoSavedWhileHiddenRef.current = true;
              } else {
                loadData(true);
              }
            }
          } catch {
            // Silent failure — localStorage draft is still there as fallback
          }
        }
      } else {
        // ── Tab visible again: if anything was auto-saved while away,
        // reload so the freshly-posted note appears in the authoritative
        // list. We only reload if at least one save succeeded; avoids a
        // gratuitous fetch every time the user alt-tabs to check Slack.
        if (autoSavedWhileHiddenRef.current) {
          autoSavedWhileHiddenRef.current = false;
          loadData(true);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isOfflineMode, persistNewNote]); // eslint-disable-line react-hooks/exhaustive-deps

  // iter_periodic_autosave — Server-side heartbeat every 60 s.
  // Covers scenarios where the user never switches tabs: running out
  // of battery, OS forced-shutdown, browser OOM crash. If localStorage
  // was also cleared (e.g. private mode, device reset), data that was
  // heartbeat-saved is still on the server.
  //
  // 2026-11 fix — also routed through `persistNewNote`, which promotes
  // the note in place on success (keeps it mounted, no data loss on a
  // keystroke made mid-flight) and dedupes against `deliveries`/`credits`
  // by id — see persistNewNote's own comment above for the full story.
  useEffect(() => {
    if (isOfflineMode) return;
    const INTERVAL_MS = 60_000;
    const id = setInterval(async () => {
      const current = newNotesRef.current || [];
      const eligible = current.filter(n => {
        if (n._key && persistedKeysRef.current.has(n._key)) return false;
        return (n.lines || []).some(l => l.beverage_product_id && parseFloat(l.quantity) > 0);
      });
      if (eligible.length === 0) return;
      let anySaved = false;
      for (const note of eligible) {
        try {
          // 2026-11 fix (4th pass) — the 60s heartbeat saves as a DRAFT
          // only (same reasoning as the tab-hidden flush above).
          const created = await persistNewNote(note, { post: false });
          if (created) anySaved = true;
        } catch {
          // Silent failure — data safe in localStorage
        }
      }
      // silent=true — see loadData's own comment: this must not unmount
      // an open, mid-edit modal via the loading spinner.
      if (anySaved) loadData(true);
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, [isOfflineMode, persistNewNote]); // eslint-disable-line react-hooks/exhaustive-deps

  // Panel-level "Add to Inventory" modal state (Feature #2 — exposes
  // the button in the card header alongside Add Delivery / Reload).
  const [panelAddFromMasterOpen, setPanelAddFromMasterOpen] = useState(false);

  // Note-level search & sort controls (client-side, offline-first)
  const [noteSearch, setNoteSearch] = useState('');
  const [noteSort, setNoteSort] = useState('supplier_date');

  // iter595 — Also guard against tab close when there are unsaved new
  // notes with entered data (not just while a save is actively in flight).
  useEffect(() => {
    const hasNewData = newNotes.some(n =>
      (n.lines || []).some(l => parseFloat(l.quantity) > 0) ||
      !!(n.supplier_id || n.reference_number || n.notes)
    );
    if (!isAnySaveInFlight && !hasNewData) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isAnySaveInFlight, newNotes]);

  // iter595 — Persist new-note drafts to localStorage on every change.
  // localStorage (not sessionStorage) persists across browser restarts,
  // tab discards, and crashes — protects auditors who switch to other
  // apps (e.g. Teams) while data is unsaved.
  // Only header fields + lines with quantities are stored to keep it lean.
  useEffect(() => {
    if (!draftKey) return;
    // 2026-11 fix — a promoted note (id set — already safely created +
    // posted on the server by persistNewNote) is no longer an "unsaved
    // draft"; keeping it here would make a browser-restart recovery try
    // to re-create it. The backend's reference-number dedup on create()
    // would catch that harmlessly, but there's no reason to carry it.
    const unsavedNotes = newNotes.filter(n => !n.id);
    if (unsavedNotes.length === 0) {
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      return;
    }
    const minimal = unsavedNotes.map(n => ({
      _key: n._key,
      _new: n._new,
      _type: n._type,
      supplier_id: n.supplier_id,
      supplier_name: n.supplier_name,
      reference_number: n.reference_number,
      date: n.date,
      notes: n.notes,
      status: n.status,
      // Only persist lines with actual qty to keep storage size small;
      // DeliveryNote re-merges the full product catalog on render.
      lines: (n.lines || []).filter(l => parseFloat(l.quantity) > 0),
    }));
    try { localStorage.setItem(draftKey, JSON.stringify(minimal)); } catch { /* ignore quota */ }
  }, [newNotes, draftKey]);

  // iter595 — Restore drafts from localStorage when the panel mounts
  // (e.g. user returns to the deliveries step after navigating away or
  // after a browser crash / tab discard).
  // Wait until products are loaded so DeliveryNote can merge saved lines
  // with the full product catalog correctly.
  useEffect(() => {
    if (!draftKey || draftRestoredRef.current || products.length === 0) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const restored = JSON.parse(raw);
      if (Array.isArray(restored) && restored.length > 0) {
        draftRestoredRef.current = true;
        setNewNotes(restored);
        toast({ title: 'Draft restored', description: 'Your unsaved delivery entries have been recovered.' });
      }
    } catch { /* ignore parse errors */ }
  }, [draftKey, products.length]); // eslint-disable-line react-hooks/exhaustive-deps


  const productMap = useMemo(() => {
    const map = {};
    products.forEach(p => { map[p.id] = p; });
    return map;
  }, [products]);

  const siteFilteredProducts = useMemo(() => {
    if (!siteProductIds || siteProductIds.size === 0) return products;
    return products.filter(p => siteProductIds.has(String(p.id)));
  }, [products, siteProductIds]);

  // Save handler exposed to parent
  useImperativeHandle(ref, () => ({
    async save() {
      // iter363 — Concurrency lock.
      // The triplication bug: when "Save & Next" is double-/triple-
      // clicked (or the wizard fires save() from multiple step-nav
      // paths), each invocation captured the same `newNotes` closure
      // (state hadn't flushed yet) and POSTed the same notes again,
      // producing 2-3 server-side copies (each with whatever the
      // user typed between clicks). `savingRef` blocks re-entry
      // while a save is in flight.
      if (savingRef.current) return;
      savingRef.current = true;
      startNoteSave();  // iter363f #8a — also tracked at panel level
      try {
        // Snapshot to local var so we don't depend on closure
        // refreshes while iterating.
        const pending = newNotes.slice();
        const savedKeys = new Set();
        // Auto-save any new notes with valid lines — routed through the
        // shared `persistNewNote` mutex (see its definition above) so
        // this loop can never race the per-note Save button, the
        // tab-hidden flush or the 60s heartbeat into a duplicate
        // create()/post() for the same note.
        for (const note of pending) {
          if (isOfflineMode) continue;
          // 2026-11 fix (3rd pass) — previously this `continue`d for any
          // key already in persistedKeysRef, which meant a note promoted
          // moments earlier by the heartbeat — with a brand-new line
          // typed into it right after — never reached persistNewNote at
          // all from this path, so its "already persisted, push latest
          // lines" branch was dead code for Save & Next specifically.
          // Always call persistNewNote with the LATEST snapshot from
          // `newNotesRef` (not the `pending` array captured at the top
          // of this function, which can already be stale by the time
          // this iteration's `await` resolves for an earlier note).
          const latestNote = (newNotesRef.current || []).find(n => n._key === note._key) || note;
          try {
            const created = await persistNewNote(latestNote);
            if (created && note._key) savedKeys.add(note._key); // iter596b — track confirmed successes
          } catch (e) {
            console.error('Auto-save delivery failed:', e);
          }
        }
        if (pending.length > 0 && !isOfflineMode) {
          // 2026-11 fix (2nd pass) — persistNewNote now PROMOTES a saved
          // note in place (id + _new:false) rather than removing it, so a
          // concurrent background save (heartbeat/visibility) can't yank
          // the modal out from under an auditor mid-edit. "Save & Next" is
          // an explicit, deliberate user action though — once the user
          // has moved on, drop the successfully-saved ones from the
          // drafts list here (existingItems dedupes by id in the
          // meantime, so nothing double-renders while this is pending).
          // A note that genuinely failed stays in the draft list instead
          // of being silently discarded.
          setNewNotes(prev => prev.filter(n => !(n._key && savedKeys.has(n._key))));
          // iter596b — Only clear the localStorage draft after CONFIRMED
          // persistence. Previously this ran unconditionally on pending.length>0,
          // losing the draft even when all saves threw server errors.
          const notesNeedingSave = pending.filter(n =>
            !(n._key && persistedKeysRef.current.has(n._key)) &&
            (n.lines || []).some(l => l.beverage_product_id && parseFloat(l.quantity) > 0)
          );
          const allSaved = notesNeedingSave.length === 0 ||
            notesNeedingSave.every(n => n._key && savedKeys.has(n._key));
          if (allSaved && draftKey) {
            try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
          }
          // silent=true — see loadData's own comment: Save & Next must not
          // unmount a DeliveryNote mid-edit via the loading spinner; that
          // unmount also clears any pending 1.5s per-note autosave timer,
          // which was the exact mechanism that lost an edit typed right
          // after a background promotion in testing.
          loadData(true);
        }
        // Handle offline saves
        if (isOfflineMode && onOfflineDeliveriesChange) {
          const offDels = pending.filter(n => n._type === 'delivery').map(n => ({
            ...n,
            entry_type: 'delivery',
            line_count: (n.lines || []).filter(l => parseFloat(l.quantity) > 0).length,
            lines_total: (n.lines || []).reduce((s, l) => s + (parseFloat(l.line_total) || 0), 0),
            lines: (n.lines || []).filter(l => parseFloat(l.quantity) > 0),
          }));
          const offCreds = pending.filter(n => n._type === 'credit').map(n => ({
            ...n,
            entry_type: 'credit',
            line_count: (n.lines || []).filter(l => parseFloat(l.quantity) > 0).length,
            lines_total: (n.lines || []).reduce((s, l) => s + (parseFloat(l.line_total) || 0), 0),
            lines: (n.lines || []).filter(l => parseFloat(l.quantity) > 0),
          }));
          if (offDels.length > 0) await onOfflineDeliveriesChange('delivery', offDels);
          if (offCreds.length > 0) await onOfflineDeliveriesChange('credit', offCreds);
        }
      } finally {
        savingRef.current = false;
        endNoteSave();
      }
      // iter363f #8a — After the parent's own save loop completes,
      // wait briefly for any silent autosaves on existing notes to
      // finish. Without this, the wizard could navigate away
      // ~1 s before a debounced autosave fires, dropping the user's
      // last keystrokes.
      const deadline = Date.now() + 5000;
      while (savingNotesCountRef.current > 0 && Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 100));
      }
    }
  }), [newNotes, siteId, periodTo, isOfflineMode, onOfflineDeliveriesChange, startNoteSave, endNoteSave, draftKey, persistNewNote]);

  const loadData = useCallback(async (silent = false) => {
    // 2026-11 fix — `silent=true` skips the loading spinner entirely.
    // Background reloads (heartbeat / tab-visibility) used to call this
    // with the default (spinner-showing) behaviour, which flips
    // `loading` to true and unmounts the ENTIRE notes list (including
    // any open, mid-edit modal) via the `(loading || productsLoading) ?
    // 'Loading deliveries…' : list` render below — that unmount is what
    // was actually discarding a keystroke made during a background
    // save's round trip, not the promotion logic itself.
    if (!silent) setLoading(true);
    if (offlineData) {
      setDeliveries((offlineData.deliveries || []).map(d => ({ ...d, lines_total: d.lines_total ?? (d.lines || []).reduce((s, l) => s + (parseFloat(l.line_total) || 0), 0) })));
      setCredits((offlineData.credits || []).map(c => ({ ...c, lines_total: c.lines_total ?? (c.lines || []).reduce((s, l) => s + (parseFloat(l.line_total) || 0), 0) })));
      setProducts(offlineData.products || []);
      setSuppliers((offlineData.suppliers || []).filter(s => s.is_active));
      const invIds = new Set((offlineData.site_inventory || []).map(i => String(i.beverage_product_id)));
      if (invIds.size > 0) setSiteProductIds(invIds);
      // iter363e — offline cost cascade: prefer the pre-packaged
      // map if the snapshot already bundled it; otherwise derive a
      // best-effort fallback from any existing offline deliveries.
      if (offlineData.delivery_cost_map && typeof offlineData.delivery_cost_map === 'object') {
        setCostMap(offlineData.delivery_cost_map);
      } else if ((offlineData.deliveries || []).length > 0) {
        const fallback = {};
        for (const d of offlineData.deliveries) {
          for (const l of (d.lines || [])) {
            const pid = String(l.beverage_product_id);
            const cost = parseFloat(l.cost_price);
            if (!cost || cost <= 0) continue;
            // first-seen wins; deliveries are ordered desc by date in the package
            if (!fallback[pid]) {
              fallback[pid] = { cost_price: cost, source: 'last_delivery', unit_label: l.unit_label || null };
            }
          }
        }
        setCostMap(fallback);
      }
      setDataSource('package');
      if (!silent) { setLoading(false); setProductsLoading(false); }
      return;
    }
    try {
      const [delRes, credRes] = await Promise.all([
        deliveriesService.list({ site_id: siteId, session_id: sessionId, date_from: periodFrom, date_to: periodTo, include_lines: true, limit: 200, sort: 'supplier' }),
        creditsService.list({ site_id: siteId, session_id: sessionId, date_from: periodFrom, date_to: periodTo, include_lines: true, limit: 200, sort: 'supplier' }),
      ]);
      setDeliveries(delRes.items || []);
      setCredits(credRes.items || []);
      setDataSource('live-api');
    } catch { /* ignore */ } finally { if (!silent) setLoading(false); }
  }, [siteId, sessionId, periodFrom, periodTo, offlineData]);

  useEffect(() => { loadData(); }, [loadData]);

  // iter081 — Pre-existing transactions warning.
  // With session_id now stored on each delivery/credit, we can precisely
  // detect notes that belong to a DIFFERENT session (carry-through). We only
  // warn about those — not about notes legitimately created in this session.
  const [showInheritWarning, setShowInheritWarning] = useState(false);
  const [inheritWarningCounts, setInheritWarningCounts] = useState({ del: 0, cred: 0 });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  useEffect(() => {
    if (loading || productsLoading) return;
    if (!isEditable) return;
    if (!sessionId) return;
    try {
      const decided = window.localStorage.getItem(`stk_sess_${sessionId}_pre_existing_dlv_decided`);
      if (decided === 'true') return;
    } catch { /* localStorage might be blocked */ }
    // Only flag records that came from a different session (session_id present
    // but != current session). Records with session_id = null are legacy
    // date-range records included intentionally; records with session_id =
    // current session were entered in this session — neither should trigger
    // the warning.
    const foreignDel = deliveries.filter(d => d.session_id != null && d.session_id !== sessionId);
    const foreignCred = credits.filter(c => c.session_id != null && c.session_id !== sessionId);
    if (foreignDel.length === 0 && foreignCred.length === 0) return;
    setInheritWarningCounts({ del: foreignDel.length, cred: foreignCred.length });
    setShowInheritWarning(true);
  }, [loading, productsLoading, deliveries, credits, isEditable, sessionId]);

  const markDecided = useCallback(() => {
    try { window.localStorage.setItem(`stk_sess_${sessionId}_pre_existing_dlv_decided`, 'true'); } catch { /* */ }
    setShowInheritWarning(false);
  }, [sessionId]);

  const handleIncludeAll = useCallback(() => {
    markDecided();
    toast({ title: 'Pre-existing transactions included', description: `${inheritWarningCounts.del + inheritWarningCounts.cred} notes will count toward this stocktake.` });
  }, [markDecided, inheritWarningCounts, toast]);

  const handleDeleteAllPreExisting = useCallback(async () => {
    if (!window.confirm(`Delete ALL ${inheritWarningCounts.del} delivery note(s) and ${inheritWarningCounts.cred} credit note(s) in this period? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      // Delete in parallel batches — the Delivery Register API accepts
      // DELETE on any unpost-then-delete cycle, but we use the bulk
      // capability where available.
      const delIds = deliveries.map(d => d.id);
      const credIds = credits.map(c => c.id);
      await Promise.all([
        ...delIds.map(id => deliveriesService.delete(id).catch(() => {})),
        ...credIds.map(id => creditsService.delete(id).catch(() => {})),
      ]);
      toast({ title: 'Pre-existing transactions deleted', description: `${delIds.length + credIds.length} notes removed.` });
      markDecided();
      await loadData();
    } catch (e) {
      toast({ title: 'Bulk delete failed', description: getApiError(e, 'One or more notes could not be deleted'), variant: 'destructive' });
    } finally {
      setBulkDeleting(false);
    }
  }, [inheritWarningCounts, deliveries, credits, markDecided, toast, loadData]);

  useEffect(() => {
    if (offlineData) return;
    Promise.all([
      suppliersService.list({ limit: 500, is_active: true }),
      beverageProductsService.listAll({}),  // iter360 — no status filter; site-inventory gate applies
      siteId ? siteInventoryService.listInventory(siteId).catch(() => ({ items: [] })) : Promise.resolve({ items: [] }),
      // iter363e — bulk delivery cost cascade for this site
      // iter_postmix — pass periodTo as before_date so only deliveries
      // before the current period end are used for cost cascade
      siteId ? deliveriesService.siteCostMap(siteId, null, periodTo || undefined).catch(() => ({})) : Promise.resolve({}),
      // iter825 — strict DN-only/site-only/unit-matched cost default,
      // used exclusively for Delivery Note lines (not credit notes).
      siteId ? deliveriesService.dnSiteCostMap(siteId, periodTo || undefined).catch(() => ({})) : Promise.resolve({}),
    ]).then(([supRes, prodRes, invRes, costRes, dnCostRes]) => {
      setSuppliers(supRes.items || []);
      setProducts(Array.isArray(prodRes) ? prodRes : (prodRes?.items || prodRes?.products || []));
      const ids = new Set((invRes?.items || invRes || []).map(i => String(i.beverage_product_id || i.id)));
      setSiteProductIds(ids);
      setCostMap(costRes || {});
      setDnCostMap(dnCostRes || {});
      setProductsLoading(false);
    }).catch(() => setProductsLoading(false));
  }, [offlineData, siteId]);

  // refreshInventory — reloads the site product IDs after a product is
  // added to site inventory from within the delivery note (Feature #1).
  // Only the inventory membership changes; the full product catalog is
  // already loaded and doesn't need to be refetched.
  const refreshInventory = useCallback(async () => {
    if (!siteId || isOfflineMode) return;
    try {
      const invRes = await siteInventoryService.listInventory(siteId);
      const ids = new Set((invRes?.items || invRes || []).map(i => String(i.beverage_product_id || i.id)));
      setSiteProductIds(ids);
    } catch { /* ignore */ }
  }, [siteId, isOfflineMode]);

  const getDefaultUnit = (prod) => {
    if (!prod?.units?.length) return '';
    // iter363f — prefer per-product purchase-unit override from Master
    const purchaseUnit = prod.units.find(u => u.is_default_purchase_unit);
    if (purchaseUnit) return purchaseUnit.unit_type;
    const stockUnit = prod.units.find(u => u.is_base_stock_unit);
    if (stockUnit) return stockUnit.unit_type;
    const singles = prod.units.find(u => u.unit_type !== 'case');
    if (singles) return singles.unit_type;
    return prod.units[0].unit_type;
  };

  // Create new delivery/credit note with all site products pre-populated
  // iter363e — Group by subcategory and pre-fill cost_price from the
  // delivery cost cascade (last delivery → opening stock → 0). Lines
  // sort by subcategory (matches the Sales panel), then alphabetical
  // within each group. The cost is still editable by the auditor.
  const addNewNote = () => {
    const sorted = [...siteFilteredProducts].sort((a, b) => {
      const subA = (a.subcategory || '').toString();
      const subB = (b.subcategory || '').toString();
      const cmp = subcategoryComparator(subA, subB);
      if (cmp !== 0) return cmp;
      return (a.product_name || '').localeCompare(b.product_name || '');
    });
    const newNote = {
      _key: `new_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      _new: true,
      _type: activeTab,
      supplier_id: '',
      supplier_name: '',
      reference_number: '',
      date: periodTo || new Date().toISOString().split('T')[0],
      notes: '',
      status: 'draft',
      lines: sorted.map(p => {
        const cascade = activeTab === 'delivery' ? dnCostMap[String(p.id)] : costMap[String(p.id)];
        // iter363e — cost cascade per the user spec:
        //   1. last delivery line cost (same site)
        //   2. opening-stock unit cost
        //   3. 0 — auditor can still override
        // iter366 — rescale cascade cost into the row's default unit
        // (e.g. bottle → case) so the displayed cost is always in the
        // SAME unit as `unit_label` below.
        // iter825 — for delivery notes, `dnCostMap` above already
        // supersedes 1+2+3 with a strict DN-line-only/same-unit/
        // same-site default (blank when no match, never opening stock).
        const targetUnit = getDefaultUnit(p);
        const cost = convertCascadeCost(cascade, targetUnit, p, activeTab === 'delivery');
        return {
          beverage_product_id: String(p.id),
          product_name: p.product_name,
          product_code: p.product_code || '',
          quantity: '',
          cost_price: cost,
          cost_source: cascade?.source || null,
          cost_delivery_date: cascade?.delivery_date || null,
          cost_delivery_ref: cascade?.reference_number || null,
          cost_delivery_status: cascade?.delivery_status || null,
          unit_label: targetUnit,
          line_total: 0,
        };
      }),
    };
    setNewNotes(prev => [newNote, ...prev]);
    // iter363e — Focus the first Qty input at the top of the new
    // note once it's painted. The data-testid pattern is
    // `delivery-qty-<productId>` (the first product in the sorted
    // list above), so we look it up rather than threading refs.
    if (sorted.length > 0) {
      setTimeout(() => {
        const firstPid = sorted[0].id;
        const qty = document.querySelector(`[data-testid="${activeTab}-qty-${firstPid}"]`);
        if (qty && typeof qty.focus === 'function') {
          qty.focus();
          if (typeof qty.select === 'function') qty.select();
        }
      }, 200);
    }
  };

  const updateNewNote = (key, updatedNote) => {
    setNewNotes(prev => prev.map(n => n._key === key ? updatedNote : n));
  };

  // ── Bug fix (2026-04-21, user report 3rd time): existing delivery/credit
  // notes had `onUpdate={() => {}}` wired up, which meant typed edits
  // (qty, unit, cost, header fields) never propagated back into the
  // `deliveries`/`credits` state. React controlled inputs then snapped
  // the visible value back to the pre-edit state — giving the user the
  // impression that the form was frozen. The Save button ran off the
  // stale state, so even if clicked no change reached the backend.
  //
  // This updater writes the edit into the correct tab's state array so
  // the inputs become truly editable and the Save handler sends the
  // user's actual values.
  const updateExistingNote = (noteId, updated) => {
    if (activeTab === 'delivery') {
      setDeliveries(prev => prev.map(d => d.id === noteId ? { ...updated, id: noteId } : d));
    } else {
      setCredits(prev => prev.map(c => c.id === noteId ? { ...updated, id: noteId } : c));
    }
  };

  const deleteNewNote = (key) => {
    if (!window.confirm('Remove this unsaved entry?')) return;
    setNewNotes(prev => prev.filter(n => n._key !== key));
  };

  const handleNoteDeleted = (noteId, noteType) => {
    if (!window.confirm(`Delete this ${noteType}? This cannot be undone.`)) return;
    const service = noteType === 'delivery' ? deliveriesService : creditsService;
    service.delete(noteId).then(() => {
      toast({ title: 'Deleted' });
      // silent — don't unmount other open/mid-edit modals just to remove
      // the one row that was deleted.
      loadData(true);
    }).catch(err => toast({ title: 'Failed', description: getApiError(err, 'Error'), variant: 'destructive' }));
  };

  const handleNoteSaved = (info) => {
    // `persistNewNote` registers the key and PROMOTES the draft in place
    // (id + _new:false) but deliberately does not remove it — only an
    // explicit, deliberate user action should do that. This callback is
    // that explicit action (the child's own Save button finished): drop
    // the entry from `newNotes` now that the user is done with it.
    if (info && info.persistedKey) {
      if (info.persistedId) persistedKeysRef.current.set(info.persistedKey, info.persistedId);
      setNewNotes(prev => prev.filter(n => n._key !== info.persistedKey));
    }
    // silent — this note is done; don't unmount any OTHER open modal
    // elsewhere in the list just to refresh this one's row.
    loadData(true);
    // iter825 — a just-saved DN's own lines may now be the most recent
    // qualifying source for the NEXT new DN added in this same session;
    // refresh the strict cost map so later placeholders see it.
    if (siteId) {
      deliveriesService.dnSiteCostMap(siteId, periodTo || undefined).then(setDnCostMap).catch(() => {});
    }
  };

  // Items for active tab
  // 2026-11 fix — a background save (heartbeat/visibility-change) now
  // promotes a new note in place (id + _new:false) instead of removing
  // it, so an in-progress edit is never lost (see persistNewNote above).
  // That means the SAME delivery can briefly exist in both `newNotes`
  // (promoted) and `deliveries`/`credits` (once loadData() reloads) —
  // dedupe by id here so it only ever renders once, as the promoted
  // (still-open, still-editable) entry. Partitioned by type since
  // delivery ids and credit ids come from separate tables and can
  // legitimately collide numerically.
  const promotedDeliveryIds = new Set(newNotes.filter(n => n._type === 'delivery' && n.id).map(n => n.id));
  const promotedCreditIds = new Set(newNotes.filter(n => n._type === 'credit' && n.id).map(n => n.id));
  const dedupedDeliveries = deliveries.filter(d => !promotedDeliveryIds.has(d.id));
  const dedupedCredits = credits.filter(c => !promotedCreditIds.has(c.id));
  const existingItems = activeTab === 'delivery' ? dedupedDeliveries : dedupedCredits;
  const newItems = newNotes.filter(n => n._type === activeTab);
  const allItems = [...newItems, ...existingItems];

  // Client-side filtered + sorted items for display (search/sort controls)
  const filteredSortedItems = useMemo(() => {
    let items = allItems.map(item => ({
      ...item,
      _displaySupplierName: item.supplier_name ||
        (item.supplier_id ? (suppliers.find(s => s.id === parseInt(item.supplier_id, 10))?.supplier_name || '') : ''),
      _displayDate: item.date || item.delivery_date || item.credit_date || '',
    }));

    if (noteSearch.trim()) {
      const q = noteSearch.trim().toLowerCase();
      items = items.filter(i =>
        (i.reference_number || '').toLowerCase().includes(q) ||
        (i._displaySupplierName || '').toLowerCase().includes(q)
      );
    }

    items.sort((a, b) => {
      if (noteSort === 'supplier_date') {
        const sup = (a._displaySupplierName || '').localeCompare(b._displaySupplierName || '');
        if (sup !== 0) return sup;
        return (a._displayDate || '').localeCompare(b._displayDate || '');
      }
      if (noteSort === 'date_desc') return (b._displayDate || '').localeCompare(a._displayDate || '');
      if (noteSort === 'date_asc') return (a._displayDate || '').localeCompare(b._displayDate || '');
      if (noteSort === 'reference') return (a.reference_number || '').localeCompare(b.reference_number || '');
      return 0;
    });

    return items;
  }, [allItems, noteSearch, noteSort, suppliers]);

  const TABS = [
    { key: 'delivery', label: 'Deliveries', icon: Truck, color: 'text-emerald-600', bg: 'bg-emerald-50', count: dedupedDeliveries.length + newNotes.filter(n => n._type === 'delivery').length },
    { key: 'credit', label: 'Credits', icon: RotateCcw, color: 'text-amber-600', bg: 'bg-amber-50', count: dedupedCredits.length + newNotes.filter(n => n._type === 'credit').length },
  ];
  const activeType = TABS.find(t => t.key === activeTab);
  // 2026-11 fix — a promoted-but-still-open note (id set, status:
  // 'posted') has no precomputed `lines_total` the way a server-loaded
  // delivery does; fall back to summing its lines directly so the
  // "Posted total" badge doesn't undercount it.
  const itemTotal = (item) => (item.lines_total != null
    ? (item.lines_total || 0)
    : (item.lines || []).reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.cost_price) || 0), 0));
  const postedTotal = allItems.filter(i => i.status === 'posted').reduce((s, i) => s + itemTotal(i), 0);

  return (
    <Card data-testid="wizard-deliveries-credits-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-emerald-600" />
            Deliveries & Credits
            {dataSource && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono border ${dataSource === 'package' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-blue-100 text-blue-700 border-blue-200'}`}>{dataSource === 'package' ? 'Offline' : 'Live'}</span>}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => loadData()} disabled={loading}
              title="Reload deliveries & credits from the server"
              data-testid="dc-reload-btn">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              <span className="ml-1 hidden sm:inline">Reload</span>
            </Button>
            {/* Feature #2 — Add Product From Inventory Master button in
                deliveries panel header. Mirrors the button inside each
                delivery note, but accessible without opening a note. */}
            {isEditable && !isOfflineMode && (
              <Button size="sm" variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={() => setPanelAddFromMasterOpen(true)}
                data-testid="dc-add-from-master-btn"
                title="Add a product from the inventory master to this site">
                <PackagePlus className="h-3.5 w-3.5 mr-1" />
                <span className="hidden sm:inline">Add to Inventory</span>
              </Button>
            )}
            {isEditable && (
              <Button size="sm" variant="default" onClick={addNewNote} data-testid={`add-new-${activeTab}-btn`}
                disabled={productsLoading || loading}
                title={productsLoading ? 'Loading products…' : undefined}>
                {productsLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                Add {activeTab === 'delivery' ? 'Delivery' : 'Credit Note'}
              </Button>
            )}
          </div>
        </div>
        <CardDescription className="text-xs">
          {periodFrom && periodTo ? `Period: ${periodFrom} to ${periodTo}` : 'Enter deliveries and credit notes for this audit period.'}
        </CardDescription>
      </CardHeader>
        {/* iter363f #8a — Panel-level saving banner. Shown while any
            delivery / credit note has an in-flight save (silent
            autosave OR the imperative `save()` from "Save & Next").
            Acts as both visual feedback and a hint that closing the
            tab now is unsafe (the beforeunload handler will also
            confirm). */}
        {isAnySaveInFlight && (
          <div
            className="mb-3 flex items-center gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
            data-testid="deliveries-saving-banner"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
            <span>
              Saving {savingNotesCount} delivery {savingNotesCount === 1 ? 'note' : 'notes'} —
              don't close the tab or move to the next step until this completes.
            </span>
          </div>
        )}

      <CardContent>
        {/* iter362h — Pre-existing transactions warning. Prevent
            silently inheriting delivery/credit notes left over from
            deleted trial stocktakes. Auditor must explicitly include
            or delete them once per session. */}
        {showInheritWarning && (
          <div
            className="mb-4 p-3 border border-amber-300 bg-amber-50 rounded-md flex flex-col gap-2"
            data-testid="pre-existing-deliveries-warning"
          >
            <div className="flex items-start gap-2">
              <FileText className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900 flex-1">
                <strong>Heads up — pre-existing transactions in this period.</strong>
                <p className="text-xs mt-0.5">
                  This audit period already contains{' '}
                  {inheritWarningCounts.del > 0 && (
                    <strong>{inheritWarningCounts.del} delivery note{inheritWarningCounts.del !== 1 ? 's' : ''}</strong>
                  )}
                  {inheritWarningCounts.del > 0 && inheritWarningCounts.cred > 0 && ' and '}
                  {inheritWarningCounts.cred > 0 && (
                    <strong>{inheritWarningCounts.cred} credit note{inheritWarningCounts.cred !== 1 ? 's' : ''}</strong>
                  )}
                  {' '}entered before this stocktake was opened (often left over from a deleted trial session). These will be counted toward this audit unless you delete them. Confirm how to proceed:
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-1">
              <Button
                size="sm"
                variant="outline"
                onClick={handleIncludeAll}
                disabled={bulkDeleting}
                data-testid="pre-existing-include-btn"
              >
                Include in this stocktake
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDeleteAllPreExisting}
                disabled={bulkDeleting}
                data-testid="pre-existing-delete-btn"
              >
                {bulkDeleting && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                Delete all pre-existing
              </Button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-3 border-b">
          {TABS.map(t => (
            <button key={t.key} onClick={() => { setActiveTab(t.key); setNoteSearch(''); }}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.key ? `border-slate-900 ${t.color}` : 'border-transparent text-muted-foreground hover:text-foreground'
              }`} data-testid={`dc-tab-${t.key}`}>
              {t.label} {t.count > 0 && <Badge variant="secondary" className="ml-1 text-xs">{t.count}</Badge>}
            </button>
          ))}
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className={`p-3 ${activeType?.bg || 'bg-slate-50'} rounded-lg`}>
            <p className="text-xs text-muted-foreground">Entries</p>
            <p className={`text-lg font-bold ${activeType?.color}`} data-testid="dc-entry-count">
              {noteSearch.trim() ? `${filteredSortedItems.length} / ${allItems.length}` : allItems.length}
            </p>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-muted-foreground">Posted Total</p>
            <p className="text-lg font-bold text-blue-700" data-testid="dc-posted-total">{fmt(postedTotal)}</p>
            <p className="text-[10px] text-muted-foreground">Used in calculation</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-muted-foreground">All Total</p>
            <p className="text-lg font-bold text-slate-600" data-testid="dc-all-total">{fmt(allItems.reduce((s, i) => s + itemTotal(i), 0))}</p>
          </div>
        </div>

        {/* Search & Sort Controls — shown when there is at least one note */}
        {allItems.length > 0 && (
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                className="pl-8 h-8 text-sm"
                placeholder="Search by supplier or note number…"
                value={noteSearch}
                onChange={e => setNoteSearch(e.target.value)}
                data-testid="dc-note-search"
              />
              {noteSearch && (
                <button
                  onClick={() => setNoteSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="dc-note-search-clear"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <Select value={noteSort} onValueChange={setNoteSort}>
              <SelectTrigger className="w-48 h-8 text-sm" data-testid="dc-note-sort">
                <SelectValue placeholder="Sort by…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="supplier_date">Supplier, then Date</SelectItem>
                <SelectItem value="date_desc">Date (Newest First)</SelectItem>
                <SelectItem value="date_asc">Date (Oldest First)</SelectItem>
                <SelectItem value="reference">Reference Number</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {(loading || productsLoading) ? (
          <div className="text-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
            {loading ? 'Loading deliveries...' : 'Loading products...'}
          </div>
        ) : (
          <div className="space-y-2">
            {allItems.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No {activeTab === 'delivery' ? 'deliveries' : 'credit notes'} for this period.</p>
                <p className="text-xs mt-1">Click "Add {activeTab === 'delivery' ? 'Delivery' : 'Credit Note'}" above to start.</p>
              </div>
            )}
            {allItems.length > 0 && filteredSortedItems.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No results for "{noteSearch}"</p>
                <button className="text-xs text-blue-600 hover:underline mt-1" onClick={() => setNoteSearch('')}>
                  Clear search
                </button>
              </div>
            )}
            {/* Unified sorted+filtered list — handles both new (unsaved) and existing notes */}
            {filteredSortedItems.map(item => (
              item._key ? (
                <DeliveryNote
                  key={item._key}
                  note={item}
                  type={activeTab}
                  products={siteFilteredProducts}
                  productMap={productMap}
                  costMap={activeTab === 'delivery' ? dnCostMap : costMap}
                  suppliers={suppliers}
                  isEditable={isEditable}
                  onUpdate={(updated) => updateNewNote(item._key, updated)}
                  onDelete={() => deleteNewNote(item._key)}
                  onSaved={handleNoteSaved}
                  onSaveStart={startNoteSave}
                  onSaveEnd={endNoteSave}
                  siteId={siteId}
                  sessionId={sessionId}
                  periodFrom={periodFrom}
                  periodTo={periodTo}
                  isOfflineMode={isOfflineMode}
                  vatRate={orgVatSettings.default_vat_rate}
                  invoiceBasis={orgVatSettings.invoice_basis}
                  onProductAdded={refreshInventory}
                  assignedProductIds={siteProductIds ? [...siteProductIds].map(Number) : []}
                  persistNewNote={persistNewNote}
                />
              ) : (
                <DeliveryNote
                  key={item.id}
                  note={{ ...item, date: item.delivery_date || item.credit_date }}
                  type={activeTab}
                  products={siteFilteredProducts}
                  productMap={productMap}
                  costMap={activeTab === 'delivery' ? dnCostMap : costMap}
                  suppliers={suppliers}
                  isEditable={isEditable}
                  onUpdate={(updated) => updateExistingNote(item.id, updated)}
                  onDelete={isEditable ? () => handleNoteDeleted(item.id, activeTab) : undefined}
                  onSaved={handleNoteSaved}
                  onSaveStart={startNoteSave}
                  onSaveEnd={endNoteSave}
                  siteId={siteId}
                  sessionId={sessionId}
                  periodFrom={periodFrom}
                  periodTo={periodTo}
                  isOfflineMode={isOfflineMode}
                  vatRate={orgVatSettings.default_vat_rate}
                  invoiceBasis={orgVatSettings.invoice_basis}
                  onProductAdded={refreshInventory}
                  assignedProductIds={siteProductIds ? [...siteProductIds].map(Number) : []}
                />
              )
            ))}
          </div>
        )}
      </CardContent>
      {/* Panel-level "Add Product From Inventory Master" modal (Feature #2).
          Opened from the card-header button. After assigning, refreshInventory
          updates siteProductIds so new product rows appear in the next note. */}
      <AddFromMasterModal
        open={panelAddFromMasterOpen}
        onOpenChange={setPanelAddFromMasterOpen}
        siteId={siteId}
        assignedProductIds={siteProductIds ? [...siteProductIds].map(Number) : []}
        onAssigned={refreshInventory}
      />
    </Card>
  );
});

export { WizardDeliveriesCreditsPanel };
